import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Doughnut, Pie, Line, Bar } from 'react-chartjs-2';
import CertBanner from './CertBanner';
import {
  Chart as ChartJS,
  CategoryScale,
  Filler,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;
const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

// 다크 글래스 팔레트 — Admin 레이아웃과 일관된 톤
const colors = {
  primary: '#01BCD6',          // 메인 cyan accent
  primaryLight: '#8BD2F4',
  success: '#22c55e',
  successLight: '#86efac',
  danger: '#f87171',
  dangerLight: '#fca5a5',
  warning: '#fbbf24',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.65)',
  textDim: 'rgba(255, 255, 255, 0.45)',
  cardBg: 'linear-gradient(180deg, rgba(15, 38, 59, 0.65) 0%, rgba(10, 25, 41, 0.78) 100%)',
  cardBorder: 'rgba(139, 210, 244, 0.12)',
  surfaceSoft: 'rgba(255, 255, 255, 0.04)',
  surfaceHover: 'rgba(255, 255, 255, 0.08)',
  // 서버별 강조 컬러 (다크 친화적)
  serverColors: { root: '#01BCD6', master: '#34d399', slave: '#a78bfa' },
};

// 다크 모드 폴백 컬러 — registry에서 받은 color가 없을 때 server_id별 매핑
const FALLBACK_COLORS = ['#01BCD6', '#34d399', '#a78bfa', '#fbbf24', '#f472b6', '#60a5fa'];
function getServerColor(server, idx = 0) {
  return server?.color || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
}

// 서버 역할 라벨 (LOCAL/MASTER/SLAVE 등)
function getServerRole(server) {
  if (!server) return { label: '—', color: '#94a3b8' };
  if (server.authType === 'local' || server.auth_type === 'local') return { label: 'LOCAL', color: '#fbbf24' };
  const sid = (server.server_id || '').toUpperCase();
  if (sid === 'MASTER') return { label: 'MASTER', color: '#a78bfa' };
  if (sid === 'SLAVE') return { label: 'SLAVE', color: '#60a5fa' };
  return { label: sid || '서버', color: server.color || colors.primary };
}

// 바이트 → 사람이 읽기 쉬운 단위
function formatBytes(bytes) {
  if (bytes == null || isNaN(bytes)) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let n = Math.abs(bytes);
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return n.toFixed(n < 10 ? 2 : n < 100 ? 1 : 0) + ' ' + u[i];
}

const DashboardContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0;
  color: ${colors.text};
  font-family: 'Noto Sans KR', 'Inter', sans-serif;
  animation: ${fadeIn} 0.5s ease-out;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const RefreshInfo = styled.div`
  font-size: 0.85rem;
  color: ${colors.textMuted};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LiveDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  background: ${colors.success};
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(34, 197, 94, 0.7);
  animation: ${pulse} 2s infinite;
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
  margin-bottom: 1.25rem;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ServerOverviewCard = styled.div`
  background: ${colors.cardBg};
  border: 1px solid ${colors.cardBorder};
  border-left: 3px solid ${props => props.color || colors.primary};
  border-radius: 10px;
  padding: 10px 12px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
  position: relative;
  &:hover {
    transform: translateY(-1px);
    border-color: ${props => props.color || colors.primary}66;
    box-shadow: 0 6px 16px rgba(0,0,0,0.28), 0 0 0 1px ${props => props.color || colors.primary}33;
  }
  ${props => props.active && `
    border-color: ${props.color || colors.primary}80;
    box-shadow: 0 4px 18px ${props.color || colors.primary}20, inset 0 0 0 1px ${props.color || colors.primary}30;
  `}
`;

const ServerName = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${props => props.color || colors.text};
  margin-bottom: 1px;
  letter-spacing: -0.2px;
  line-height: 1.25;
  /* 우측 status badge 자리 확보 */
  padding-right: 60px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ServerIp = styled.div`
  font-size: 0.7rem;
  color: ${colors.textDim};
  margin-bottom: 8px;
  font-family: 'Fira Code', 'Courier New', monospace;
`;

const MiniMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
`;

const MiniMetric = styled.div`
  text-align: center;
  min-width: 0;
`;

const MiniLabel = styled.div`
  font-size: 0.58rem;
  color: ${colors.textDim};
  margin-bottom: 1px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const MiniValue = styled.div`
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.1;
  color: ${props => {
    if (props.danger) return colors.danger;
    if (props.warn) return colors.warning;
    return colors.text;
  }};
`;

const NetworkRow = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed rgba(255,255,255,0.06);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  .l {
    font-size: 0.58rem;
    color: ${colors.textDim};
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .v {
    font-size: 0.88rem;
    font-weight: 700;
    color: ${props => props.local ? colors.primaryLight : '#fff'};
    font-feature-settings: 'tnum';
  }
`;

const RolePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 7px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: ${props => (props.color || colors.primary) + '1f'};
  color: ${props => props.color || colors.primary};
  border: 1px solid ${props => (props.color || colors.primary) + '4d'};
  white-space: nowrap;
  .label { color: ${colors.textDim}; font-weight: 500; font-size: 0.66rem; text-transform: none; letter-spacing: 0; }
`;

const ServerCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed rgba(255,255,255,0.06);
`;

const SmallSection = styled.div`
  background: ${colors.cardBg};
  border: 1px solid ${colors.cardBorder};
  border-radius: 14px;
  padding: 14px 16px;
  backdrop-filter: blur(20px);
`;

const SmallHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255,255,255,0.05);

  .right { font-size: 0.74rem; color: ${colors.textDim}; font-weight: 500; }
`;

const SummaryStats = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.cols || 4}, 1fr);
  gap: 8px;
  margin-bottom: 12px;

  .item {
    background: rgba(7, 18, 35, 0.5);
    border: 1px solid rgba(255,255,255,0.05);
    border-radius: 8px;
    padding: 8px 10px;
    text-align: center;
  }
  .item .l {
    font-size: 0.62rem;
    color: ${colors.textDim};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 3px;
  }
  .item .v {
    font-size: 1rem;
    font-weight: 800;
    color: ${props => props.color || '#fff'};
    line-height: 1.1;
    font-feature-settings: 'tnum';
  }
  .item .s {
    font-size: 0.62rem;
    color: ${colors.textDim};
    margin-top: 2px;
  }
`;

const TopTableMini = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  .row {
    display: grid;
    grid-template-columns: 16px 1fr auto;
    gap: 8px;
    align-items: center;
    font-size: 0.78rem;

    .num { color: ${colors.textDim}; font-feature-settings: 'tnum'; }
    .name { color: #fff; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .val { color: ${colors.primaryLight}; font-weight: 700; font-feature-settings: 'tnum'; }
    .bar {
      grid-column: 2 / 3;
      height: 3px;
      border-radius: 999px;
      background: rgba(255,255,255,0.06);
      margin-top: 2px;
      overflow: hidden;
    }
    .bar span {
      display: block;
      height: 100%;
      width: ${props => Math.min(100, props.percent || 0)}%;
      background: ${colors.primary};
      border-radius: 999px;
    }
  }
`;

const StatusBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 0.62rem;
  padding: 1px 7px;
  border-radius: 999px;
  font-weight: 600;
  letter-spacing: 0.2px;
  background: ${props => props.online ? 'rgba(34, 197, 94, 0.14)' : 'rgba(248, 113, 113, 0.14)'};
  color: ${props => props.online ? colors.successLight : colors.dangerLight};
  border: 1px solid ${props => props.online ? 'rgba(34, 197, 94, 0.3)' : 'rgba(248, 113, 113, 0.3)'};
`;

const TabContainer = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

const MonitorTabBar = styled.div`
  display: flex;
  gap: 2px;
  margin: 0 0 14px;
  padding: 5px;
  background: rgba(7, 18, 35, 0.55);
  border: 1px solid ${colors.cardBorder};
  border-radius: 14px;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  width: fit-content;
  max-width: 100%;
  overflow-x: auto;
`;

const MonitorTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border: 0;
  border-radius: 10px;
  background: ${props => props.active
    ? `linear-gradient(135deg, ${colors.primary}26, ${colors.primary}12)`
    : 'transparent'};
  color: ${props => props.active ? colors.primaryLight : colors.textMuted};
  font-family: inherit;
  font-size: 0.88rem;
  font-weight: ${props => props.active ? 700 : 500};
  cursor: pointer;
  transition: all 0.18s ease;
  letter-spacing: -0.1px;
  white-space: nowrap;
  border: 1px solid ${props => props.active ? `${colors.primary}55` : 'transparent'};
  &:hover {
    color: ${colors.primaryLight};
    background: ${props => props.active
      ? `linear-gradient(135deg, ${colors.primary}26, ${colors.primary}12)`
      : 'rgba(255,255,255,0.04)'};
  }
`;

const SubTabBar = styled.div`
  display: flex;
  gap: 2px;
  margin: -4px 0 14px;
  padding: 4px;
  background: rgba(7, 18, 35, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  width: fit-content;
`;

const SubTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border: 0;
  border-radius: 9px;
  background: ${props => props.active ? `${colors.primary}1a` : 'transparent'};
  color: ${props => props.active ? colors.primaryLight : colors.textMuted};
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: ${props => props.active ? 700 : 500};
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: -0.1px;
  &:hover {
    color: ${colors.primaryLight};
    background: ${props => props.active ? `${colors.primary}1a` : 'rgba(255,255,255,0.04)'};
  }
  .badge {
    display: inline-flex;
    align-items: center;
    padding: 1px 7px;
    border-radius: 999px;
    background: ${props => props.active ? 'rgba(1, 188, 214, 0.2)' : 'rgba(255,255,255,0.06)'};
    color: ${props => props.active ? colors.primaryLight : colors.textDim};
    font-size: 0.7rem;
    font-weight: 700;
    margin-left: 2px;
  }
`;

const Tab = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.active ? `${props.color || colors.primary}55` : 'rgba(255,255,255,0.08)'};
  background: ${props => props.active ? `${props.color || colors.primary}1a` : 'rgba(255,255,255,0.04)'};
  color: ${props => props.active ? (props.color || colors.primary) : colors.textMuted};
  font-weight: ${props => props.active ? '700' : '500'};
  font-size: 0.86rem;
  cursor: pointer;
  border-radius: 999px;
  font-family: inherit;
  transition: all 0.15s;
  &:hover {
    color: ${props => props.color || colors.primary};
    background: ${props => props.color || colors.primary}1a;
    border-color: ${props => props.color || colors.primary}55;
  }
  @media (max-width: 768px) { padding: 6px 12px; font-size: 0.8rem; }
`;

const SectionContainer = styled.section`
  background: ${colors.cardBg};
  border: 1px solid ${colors.cardBorder};
  border-radius: 16px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  padding: 18px 20px 20px;
  margin-bottom: 14px;
  animation: ${slideUp} 0.3s ease-out;
  @media (max-width: 768px) { padding: 14px; margin-bottom: 12px; }
`;

const SectionTitle = styled.h2`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${colors.text};
  margin: 0 0 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  letter-spacing: -0.2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  @media (max-width: 768px) { font-size: 0.9rem; }
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
  @media (max-width: 768px) { grid-template-columns: 1fr; gap: 12px; }
`;

const ChartCard = styled.div`
  background: rgba(7, 18, 35, 0.55);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  height: 260px;
  transition: border-color 0.18s ease;
  &:hover { border-color: rgba(139, 210, 244, 0.18); }
  @media (max-width: 768px) { height: 240px; }
`;

const ChartTitle = styled.h3`
  font-size: 0.82rem;
  color: ${colors.textMuted};
  margin: 0 0 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ChartWrapper = styled.div`
  flex-grow: 1;
  min-height: 0;
  position: relative;
`;

const MetricValue = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${props => props.color || colors.primary};
  margin-top: 0.5rem;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.05);
  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-track { background: rgba(255,255,255,0.02); }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 3px; }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
  th {
    background: rgba(255,255,255,0.03);
    padding: 10px 14px;
    text-align: left;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    font-weight: 600;
    color: ${colors.textDim};
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }
  td {
    padding: 11px 14px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.8);
    white-space: nowrap;
  }
  tr:hover td { background: rgba(1, 188, 214, 0.04); }
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  background: ${props => {
    if (props.status === 'online') return colors.success;
    if (props.status === 'errored') return colors.danger;
    return colors.warning;
  }};
  box-shadow: 0 0 8px ${props => {
    if (props.status === 'online') return 'rgba(34,197,94,0.6)';
    if (props.status === 'errored') return 'rgba(248,113,113,0.6)';
    return 'rgba(251,191,36,0.6)';
  }};
`;

const ActionBtn = styled.button`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  margin-right: 0.25rem;
  font-family: inherit;
  transition: all 0.15s;
  border: 1px solid;
  &.restart {
    background: rgba(34,197,94,0.1);
    border-color: rgba(34,197,94,0.3);
    color: ${colors.successLight};
    &:hover { background: rgba(34,197,94,0.2); }
  }
  &.stop {
    background: rgba(248,113,113,0.1);
    border-color: rgba(248,113,113,0.3);
    color: ${colors.dangerLight};
    &:hover { background: rgba(248,113,113,0.2); }
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const LogContainer = styled.div`
  background-color: #050d1a;
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 10px;
  padding: 14px 16px;
  max-height: 400px;
  overflow-y: auto;
  font-family: 'Fira Code', 'Courier New', monospace;
  font-size: 0.78rem;
  color: rgba(232,236,255,0.78);
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.55;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: rgba(255,255,255,0.02); }
  &::-webkit-scrollbar-thumb { background: rgba(139,210,244,0.2); border-radius: 3px; }
`;

const LogControls = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 0.85rem;
  flex-wrap: wrap;
  align-items: center;
`;

const LogBtn = styled.button`
  padding: 6px 12px;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  background: ${props => props.active ? `${colors.primary}26` : 'rgba(255,255,255,0.04)'};
  color: ${props => props.active ? colors.primaryLight : colors.textMuted};
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;
  font-family: inherit;
  transition: all 0.15s;
  &:hover { border-color: rgba(139,210,244,0.4); color: ${colors.primaryLight}; }
  &.error {
    background: ${props => props.active ? 'rgba(248,113,113,0.18)' : 'rgba(255,255,255,0.04)'};
    color: ${props => props.active ? colors.dangerLight : colors.textMuted};
    &:hover { border-color: rgba(248,113,113,0.4); color: ${colors.dangerLight}; }
  }
`;

const Select = styled.select`
  padding: 6px 10px;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  font-size: 0.78rem;
  background: rgba(7, 18, 35, 0.7);
  color: ${colors.text};
  cursor: pointer;
  font-family: inherit;
  &:hover { border-color: rgba(139,210,244,0.3); }
  option { background: #0a1929; color: #fff; }
`;

const DumpSection = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const DumpButton = styled.button`
  background: linear-gradient(135deg, ${colors.primary}, #0097a7);
  color: #fff;
  padding: 9px 18px;
  border: none;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  letter-spacing: 0.2px;
  box-shadow: 0 4px 14px rgba(1, 188, 214, 0.25);
  transition: all 0.15s;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(1, 188, 214, 0.35);
  }
  &:disabled {
    background: rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.4);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

const InfoCard = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 0.82rem;
  p {
    margin: 6px 0;
    line-height: 1.5;
    color: rgba(255,255,255,0.85);
    display: flex;
    gap: 8px;
  }
  strong {
    color: ${colors.textDim};
    min-width: 110px;
    font-weight: 500;
    font-size: 0.78rem;
  }
`;

const StatPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.2px;
`;

/* KPI 그리드 (탭 상단 요약 카드) */
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
  margin-bottom: 18px;
`;

const KpiCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  background:
    radial-gradient(circle at top right, ${props => (props.accent || colors.primary) + '14'} 0%, transparent 60%),
    linear-gradient(180deg, rgba(15, 38, 59, 0.8) 0%, rgba(7, 18, 35, 0.85) 100%);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 14px;
  padding: 14px 16px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    border-color: ${props => (props.accent || colors.primary) + '40'};
    box-shadow:
      0 12px 28px rgba(0,0,0,0.32),
      0 0 0 1px ${props => (props.accent || colors.primary) + '22'};
  }
`;

const KpiIcon = styled.div`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  background: ${props => (props.accent || colors.primary) + '1f'};
  color: ${props => props.accent || colors.primary};
  border: 1px solid ${props => (props.accent || colors.primary) + '33'};
  box-shadow: 0 0 16px ${props => (props.accent || colors.primary) + '20'} inset;
`;

const KpiBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const KpiLabel = styled.div`
  font-size: 0.68rem;
  color: rgba(255,255,255,0.48);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const KpiValue = styled.div`
  font-size: 1.35rem;
  font-weight: 800;
  color: ${props => props.color || '#fff'};
  letter-spacing: -0.5px;
  line-height: 1.15;
  font-feature-settings: 'tnum';
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const KpiSub = styled.div`
  font-size: 0.68rem;
  color: rgba(255,255,255,0.4);
  letter-spacing: 0.1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* 프로세스 탭 — 좌:테이블 / 우:사이드바 */
const ProcLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 14px;
  margin-bottom: 14px;
  @media (max-width: 1280px) { grid-template-columns: 1fr; }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const SmallSearch = styled.div`
  position: relative;
  flex: 1;
  min-width: 180px;
  max-width: 260px;

  input {
    width: 100%;
    padding: 8px 12px 8px 32px;
    background: rgba(7, 18, 35, 0.7);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 8px;
    color: #fff;
    font-size: 0.82rem;
    font-family: inherit;
    box-sizing: border-box;
    &:focus {
      outline: none;
      border-color: rgba(139,210,244,0.4);
    }
    &::placeholder { color: rgba(255,255,255,0.3); }
  }
  svg {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(255,255,255,0.35);
    pointer-events: none;
  }
`;

const RefreshBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  background: rgba(255,255,255,0.04);
  color: ${colors.textMuted};
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { color: ${colors.primaryLight}; border-color: rgba(139,210,244,0.4); }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  font-size: 0.82rem;
  color: ${colors.textMuted};
`;

const PageBtn = styled.button`
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid ${props => props.active ? `${colors.primary}55` : 'rgba(255,255,255,0.08)'};
  background: ${props => props.active ? `${colors.primary}1a` : 'rgba(255,255,255,0.03)'};
  color: ${props => props.active ? colors.primaryLight : colors.textMuted};
  cursor: pointer;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: ${props => props.active ? 700 : 500};
  transition: all 0.15s;
  &:hover:not(:disabled) { color: ${colors.primaryLight}; border-color: rgba(139,210,244,0.4); }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const SidePanel = styled.div`
  background: ${colors.cardBg};
  border: 1px solid ${colors.cardBorder};
  border-radius: 14px;
  padding: 16px 18px;
  backdrop-filter: blur(20px);
  margin-bottom: 14px;
`;

const SidePanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 0.82rem;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TopList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  column-gap: 10px;
  row-gap: 4px;
  align-items: center;

  .name { font-size: 0.84rem; color: #fff; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .val { font-size: 0.74rem; color: ${props => props.color || colors.primaryLight}; font-weight: 700; font-feature-settings: 'tnum'; text-align: right; }
  .bar {
    grid-column: 1 / -1;
    height: 4px;
    border-radius: 999px;
    background: rgba(255,255,255,0.06);
    overflow: hidden;
    span {
      display: block;
      height: 100%;
      width: ${props => Math.min(100, props.percent || 0)}%;
      background: ${props => props.color || colors.primary};
      border-radius: 999px;
      transition: width 0.4s ease;
    }
  }
`;

const StatusLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.82rem;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  .dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    background: ${props => props.color || colors.primary};
  }
  .lbl { color: rgba(255,255,255,0.78); flex: 1; }
  .val { color: #fff; font-weight: 700; font-feature-settings: 'tnum'; }
  .pct { color: ${colors.textDim}; font-size: 0.74rem; margin-left: 4px; }
`;

const LogToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: ${colors.textMuted};
  cursor: pointer;
  user-select: none;

  input { display: none; }
  .track {
    width: 32px;
    height: 18px;
    background: rgba(255,255,255,0.1);
    border-radius: 999px;
    position: relative;
    transition: background 0.18s ease;
  }
  .track::after {
    content: '';
    position: absolute;
    top: 2px; left: 2px;
    width: 14px; height: 14px;
    background: #fff;
    border-radius: 50%;
    transition: transform 0.18s ease;
  }
  input:checked + .track { background: ${colors.primary}; }
  input:checked + .track::after { transform: translateX(14px); }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: ${props => props.bg || 'rgba(34,197,94,0.16)'};
  color: ${props => props.color || colors.successLight};
  border: 1px solid ${props => props.border || 'rgba(34,197,94,0.3)'};
  &::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.85;
  }
`;

/* 도넛 차트 중앙 텍스트 오버레이 */
const ChartCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  padding-bottom: 38px;  /* 범례 영역 보정 */
  text-align: center;

  .big {
    font-size: 1.55rem;
    font-weight: 800;
    color: ${props => props.color || '#fff'};
    letter-spacing: -0.5px;
    line-height: 1;
    font-feature-settings: 'tnum';
  }
  .small {
    font-size: 0.74rem;
    color: rgba(255,255,255,0.5);
    margin-top: 4px;
    font-feature-settings: 'tnum';
  }
`;

/* 상세 키-값 그리드 (시스템 정보용) */
const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
`;

const DetailItem = styled.div`
  background: rgba(7, 18, 35, 0.45);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .k {
    font-size: 0.66rem;
    color: rgba(255,255,255,0.42);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }
  .v {
    font-size: 0.92rem;
    color: #fff;
    font-weight: 600;
    font-feature-settings: 'tnum';
    word-break: break-all;
  }
  .v.mono {
    font-family: 'Fira Code', 'Courier New', monospace;
    font-size: 0.82rem;
  }
`;

/* DB 통계 카드 */
const DbStatBlock = styled.div`
  background: rgba(7, 18, 35, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 14px 16px;
`;

/* DB 탭 — 서버 선택 카드 */
const DbServerScroller = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 4px 2px 12px;
  scroll-snap-type: x mandatory;
  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-thumb { background: rgba(139,210,244,0.2); border-radius: 3px; }
`;

const DbServerCard = styled.div`
  flex: 0 0 320px;
  scroll-snap-align: start;
  background:
    radial-gradient(circle at top right, ${props => (props.accent || colors.primary) + '14'} 0%, transparent 60%),
    linear-gradient(180deg, rgba(15, 38, 59, 0.85) 0%, rgba(7, 18, 35, 0.9) 100%);
  border: 1px solid ${props => props.active ? `${props.accent || colors.primary}66` : 'rgba(255,255,255,0.06)'};
  border-radius: 14px;
  padding: 14px 16px;
  cursor: pointer;
  position: relative;
  transition: all 0.18s ease;
  ${props => props.active && `box-shadow: 0 0 0 1px ${props.accent || colors.primary}55, 0 8px 24px ${props.accent || colors.primary}22;`}
  &:hover {
    border-color: ${props => (props.accent || colors.primary) + '88'};
    transform: translateY(-1px);
  }
`;

const DbServerHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;

  .icon {
    width: 30px; height: 30px;
    border-radius: 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${props => (props.accent || colors.primary) + '22'};
    border: 1px solid ${props => (props.accent || colors.primary) + '44'};
    font-size: 0.95rem;
  }
  .name {
    flex: 1;
    min-width: 100px;
    color: #fff;
    font-weight: 700;
    font-size: 0.92rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DbRoleBadge = styled.span`
  font-size: 0.66rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 5px;
  letter-spacing: 0.4px;
  background: ${props => props.bg || 'rgba(1, 188, 214, 0.18)'};
  color: ${props => props.color || colors.primaryLight};
  border: 1px solid ${props => props.border || 'rgba(1, 188, 214, 0.35)'};
  text-transform: uppercase;
`;

const DbServerMini = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 6px;
  font-feature-settings: 'tnum';

  .col { text-align: left; }
  .col .l {
    font-size: 0.62rem;
    color: rgba(255,255,255,0.4);
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 2px;
  }
  .col .v {
    font-size: 0.88rem;
    color: #fff;
    font-weight: 700;
    line-height: 1.1;
  }
  .col .v .u { font-size: 0.66rem; font-weight: 500; color: rgba(255,255,255,0.45); margin-left: 2px; }
`;

/* 노드 트리 */
const TreeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.84rem;
`;

const TreeNode = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(7, 18, 35, 0.4);
  margin-left: ${props => (props.level || 0) * 24}px;
  position: relative;
  border: 1px solid rgba(255,255,255,0.04);

  &:hover { background: rgba(1, 188, 214, 0.05); }

  .ico {
    width: 24px; height: 24px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${props => (props.accent || '#94a3b8') + '22'};
    border: 1px solid ${props => (props.accent || '#94a3b8') + '40'};
    font-size: 0.78rem;
    flex-shrink: 0;
  }
  .name {
    color: #fff;
    font-weight: 600;
    font-size: 0.86rem;
  }
  .addr {
    font-family: 'Fira Code', monospace;
    color: rgba(255,255,255,0.45);
    font-size: 0.74rem;
  }
  .lag {
    margin-left: auto;
    font-size: 0.72rem;
    color: ${colors.textDim};
    text-align: right;
  }
  .lag .v { display: block; color: rgba(255,255,255,0.78); font-weight: 600; }
`;

/* 리소스 progress 그리드 (DB 리소스 사용률 카드) */
const ResourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
  align-items: end;
`;

const ResourceItem = styled.div`
  display: flex;
  flex-direction: column;

  .lbl {
    font-size: 0.78rem;
    color: rgba(255,255,255,0.65);
    font-weight: 600;
    margin-bottom: 6px;
  }
  .val {
    font-size: 1.35rem;
    font-weight: 800;
    color: ${props => props.color || '#fff'};
    line-height: 1.1;
    font-feature-settings: 'tnum';
    margin-bottom: 8px;
  }
  .bar {
    width: 100%;
    height: 6px;
    border-radius: 999px;
    background: rgba(255,255,255,0.06);
    overflow: hidden;
    margin-bottom: 6px;
  }
  .bar span {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: ${props => props.color || colors.primary};
    width: ${props => Math.min(100, props.percent || 0)}%;
    transition: width 0.3s ease;
  }
  .sub {
    font-size: 0.72rem;
    color: rgba(255,255,255,0.4);
    font-feature-settings: 'tnum';
  }
`;

/* 진행 막대 (연결 풀 사용량 등) */
const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255,255,255,0.06);
  border-radius: 999px;
  overflow: hidden;
  margin: 6px 0 4px;

  span {
    display: block;
    height: 100%;
    background: linear-gradient(90deg, ${props => props.accent || colors.primary}, ${props => props.accent || colors.primary}aa);
    width: ${props => Math.min(100, props.value || 0)}%;
    transition: width 0.3s ease;
  }
`;

const ManageBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid rgba(139, 210, 244, 0.3);
  background: rgba(1, 188, 214, 0.1);
  color: ${colors.primaryLight};
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: rgba(1, 188, 214, 0.2);
    border-color: rgba(139, 210, 244, 0.5);
  }
`;

/* Modal */
const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(5, 13, 26, 0.78);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ModalCard = styled.div`
  background: linear-gradient(180deg, #0F263B 0%, #0a1929 100%);
  border: 1px solid rgba(139, 210, 244, 0.2);
  border-radius: 18px;
  width: min(720px, 100%);
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.55);
  animation: ${slideUp} 0.25s cubic-bezier(0.22, 1, 0.36, 1);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.2px;
`;

const ModalClose = styled.button`
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background: rgba(255,255,255,0.06);
  color: #fff;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: rgba(255,255,255,0.14); transform: rotate(90deg); }
`;

const ModalBody = styled.div`
  padding: 18px 22px;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: rgba(139,210,244,0.2); border-radius: 3px; }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 22px;
  border-top: 1px solid rgba(255,255,255,0.06);
`;

const SearchBar = styled.div`
  position: relative;
  margin-bottom: 14px;

  input {
    width: 100%;
    padding: 11px 38px 11px 38px;
    background: rgba(7, 18, 35, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    color: #fff;
    font-size: 0.88rem;
    font-family: inherit;
    box-sizing: border-box;
    transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  }
  input::placeholder { color: rgba(255, 255, 255, 0.3); }
  input:focus {
    outline: none;
    border-color: rgba(139, 210, 244, 0.5);
    background: rgba(7, 18, 35, 0.9);
    box-shadow: 0 0 0 3px rgba(1, 188, 214, 0.12);
  }

  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: ${colors.textDim};
    display: flex;
  }

  .search-clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 0;
    background: rgba(255, 255, 255, 0.06);
    color: ${colors.textMuted};
    cursor: pointer;
    font-size: 0.85rem;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;
  }
  .search-clear:hover {
    background: rgba(248, 113, 113, 0.18);
    color: ${colors.dangerLight};
  }
`;

const SearchMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.74rem;
  color: ${colors.textDim};
  margin-bottom: 8px;
  letter-spacing: 0.2px;

  strong {
    color: ${colors.primaryLight};
    font-weight: 700;
  }
`;

const RegistryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RegistryRow = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr auto auto;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-left: 3px solid ${props => props.color || colors.primary};
  border-radius: 10px;
  transition: background 0.15s;
  &:hover { background: rgba(1, 188, 214, 0.06); }
`;

const RowMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  .name { color: #fff; font-weight: 600; font-size: 0.9rem; }
  .sub { color: ${colors.textDim}; font-size: 0.76rem; font-family: 'Fira Code', monospace; }
`;

const RowBadge = styled.span`
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
  background: ${props => props.local ? 'rgba(1, 188, 214, 0.14)' : 'rgba(167, 139, 250, 0.14)'};
  color: ${props => props.local ? colors.primaryLight : '#c4b5fd'};
  border: 1px solid ${props => props.local ? 'rgba(1, 188, 214, 0.3)' : 'rgba(167, 139, 250, 0.3)'};
`;

const RowActions = styled.div`
  display: flex;
  gap: 4px;
`;

const IconBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.08);
  background: rgba(255,255,255,0.04);
  color: ${colors.textMuted};
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.15s;
  &:hover { background: rgba(1, 188, 214, 0.12); color: ${colors.primaryLight}; border-color: rgba(139,210,244,0.3); }
  &.danger:hover { background: rgba(248,113,113,0.14); color: ${colors.dangerLight}; border-color: rgba(248,113,113,0.3); }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const FormField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${props => props.full && 'grid-column: 1 / -1;'}
  span {
    font-size: 0.75rem;
    color: ${colors.textDim};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }
`;

const FormInput = styled.input`
  padding: 10px 12px;
  background: rgba(7, 18, 35, 0.7);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 0.88rem;
  font-family: inherit;
  &:focus {
    outline: none;
    border-color: rgba(139,210,244,0.5);
    background: rgba(7, 18, 35, 0.9);
  }
  &::placeholder { color: rgba(255,255,255,0.25); }
`;

const FormTextarea = styled.textarea`
  padding: 10px 12px;
  background: rgba(7, 18, 35, 0.7);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 0.78rem;
  font-family: 'D2Coding', 'Consolas', monospace;
  line-height: 1.5;
  min-height: 150px;
  resize: vertical;
  white-space: pre;
  overflow-wrap: normal;
  overflow-x: auto;
  &:focus {
    outline: none;
    border-color: rgba(139,210,244,0.5);
    background: rgba(7, 18, 35, 0.9);
  }
  &::placeholder { color: rgba(255,255,255,0.25); }
`;

const FormHint = styled.small`
  color: ${colors.textDim};
  font-size: 0.72rem;
  line-height: 1.5;
  text-transform: none;
  letter-spacing: 0;
`;

const FormSelect = styled.select`
  padding: 10px 12px;
  background: rgba(7, 18, 35, 0.7);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 0.88rem;
  font-family: inherit;
  cursor: pointer;
  &:focus { outline: none; border-color: rgba(139,210,244,0.5); }
  option { background: #0a1929; color: #fff; }
`;

const PrimaryBtn = styled.button`
  padding: 9px 18px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, ${colors.primary}, #0097a7);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(1, 188, 214, 0.25);
  transition: all 0.15s;
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(1, 188, 214, 0.35); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }
`;

const GhostBtn = styled.button`
  padding: 9px 18px;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px;
  background: transparent;
  color: ${colors.textMuted};
  font-size: 0.85rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: rgba(255,255,255,0.06); color: #fff; }
`;

// 차트 다크 모드 옵션
const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '70%',
  plugins: {
    legend: { position: 'bottom', labels: { color: 'rgba(255,255,255,0.6)', font: { size: 10 }, boxWidth: 10 } },
    tooltip: {
      backgroundColor: 'rgba(7,18,35,0.95)',
      borderColor: 'rgba(139,210,244,0.2)',
      borderWidth: 1,
      callbacks: {
        label: (ctx) => {
          const val = ctx.parsed;
          const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
          return `${ctx.label}: ${((val / total) * 100).toFixed(1)}%`;
        },
      },
    },
  },
};

const lineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { color: 'rgba(255,255,255,0.6)', font: { size: 10 }, boxWidth: 10 } },
    tooltip: {
      backgroundColor: 'rgba(7,18,35,0.95)',
      borderColor: 'rgba(139,210,244,0.2)',
      borderWidth: 1,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: 'rgba(255,255,255,0.4)', font: { size: 9 } },
    },
    y: {
      beginAtZero: true,
      max: 100,
      grid: { color: 'rgba(255,255,255,0.05)' },
      ticks: { color: 'rgba(255,255,255,0.4)', font: { size: 9 } },
    },
  },
};

const API = '/api/admin/server';

function RuleForm({ rule, servers, onCancel, onSave }) {
  const [f, setF] = React.useState(rule);
  const isNew = !f.id;
  return (
    <FormGrid>
      <FormField full>
        <span>룰 이름 *</span>
        <FormInput value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="예: CPU 위험 (90%)" />
      </FormField>
      <FormField>
        <span>대상 서버</span>
        <FormSelect value={f.server_id || ''} onChange={e => setF({ ...f, server_id: e.target.value || null })}>
          <option value="">전체 서버</option>
          {servers.map(s => <option key={s.server_id} value={s.server_id}>{s.name}</option>)}
        </FormSelect>
      </FormField>
      <FormField>
        <span>메트릭</span>
        <FormSelect value={f.metric} onChange={e => setF({ ...f, metric: e.target.value })} disabled={!isNew}>
          <option value="cpu">CPU 사용률 (%)</option>
          <option value="memory">메모리 사용률 (%)</option>
          <option value="disk">디스크 사용률 (%)</option>
          <option value="ping">핑 (ms)</option>
          <option value="down">서버 다운</option>
        </FormSelect>
      </FormField>
      <FormField>
        <span>비교</span>
        <FormSelect value={f.comparator} onChange={e => setF({ ...f, comparator: e.target.value })}>
          <option value=">">초과 ({'>'}){''}</option>
          <option value="<">미만 ({'<'}){''}</option>
          <option value=">=">이상 (≥)</option>
          <option value="<=">이하 (≤)</option>
        </FormSelect>
      </FormField>
      <FormField>
        <span>임계값</span>
        <FormInput type="number" step="0.1" value={f.threshold} onChange={e => setF({ ...f, threshold: parseFloat(e.target.value) })} />
      </FormField>
      <FormField>
        <span>지속 시간 (초)</span>
        <FormInput type="number" min="0" value={f.duration_sec} onChange={e => setF({ ...f, duration_sec: parseInt(e.target.value) || 0 })} />
      </FormField>
      <FormField>
        <span>심각도</span>
        <FormSelect value={f.severity} onChange={e => setF({ ...f, severity: e.target.value })}>
          <option value="info">INFO</option>
          <option value="warning">WARNING</option>
          <option value="critical">CRITICAL</option>
        </FormSelect>
      </FormField>
      <FormField full>
        <span>&nbsp;</span>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <GhostBtn onClick={onCancel}>취소</GhostBtn>
          <PrimaryBtn onClick={() => onSave(f)}>{isNew ? '추가' : '저장'}</PrimaryBtn>
        </div>
      </FormField>
    </FormGrid>
  );
}

function ChannelForm({ channel, onCancel, onSave }) {
  const [f, setF] = React.useState(channel);
  const isNew = !f.id;
  return (
    <FormGrid>
      <FormField full>
        <span>채널 이름 *</span>
        <FormInput value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="예: 운영팀 알람방" />
      </FormField>
      <FormField>
        <span>타입</span>
        <FormSelect value={f.type} onChange={e => setF({ ...f, type: e.target.value })}>
          <option value="webhook">AntsTalk Webhook</option>
        </FormSelect>
      </FormField>
      <FormField>
        <span>최소 심각도</span>
        <FormSelect value={f.min_severity} onChange={e => setF({ ...f, min_severity: e.target.value })}>
          <option value="info">INFO 이상</option>
          <option value="warning">WARNING 이상</option>
          <option value="critical">CRITICAL만</option>
        </FormSelect>
      </FormField>
      <FormField full>
        <span>AntsTalk Webhook URL *</span>
        <FormInput
          value={f.url || ''}
          onChange={e => setF({ ...f, url: e.target.value })}
          placeholder="https://talk.antsnest.co.kr/chat/incoming-webhook/<token>"
          type="url"
        />
      </FormField>
      <FormField full>
        <div style={{ background: 'rgba(7, 18, 35, 0.5)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '10px 12px', fontSize: '0.74rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>
          💡 <strong style={{ color: '#fff' }}>AntsTalk webhook 만들기</strong>: AntsTalk에 로그인한 상태에서 알람 받을 채팅방을 정한 뒤,
          <code style={{ display: 'block', marginTop: 4, fontFamily: 'Fira Code, monospace', fontSize: '0.72rem', color: '#8BD2F4' }}>
            POST https://talk.antsnest.co.kr/chat/incoming-webhooks
            <br />Body: {'{ "name": "서버 알람", "room_id": "<채팅방-uuid>" }'}
          </code>
          응답으로 받은 token을 위 URL의 끝에 붙이면 됩니다. (앞으로 이 화면에서 직접 만들 수 있게 작업 예정)
        </div>
      </FormField>
      <FormField full>
        <span>&nbsp;</span>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <GhostBtn onClick={onCancel}>취소</GhostBtn>
          <PrimaryBtn onClick={() => onSave(f)}>{isNew ? '추가' : '저장'}</PrimaryBtn>
        </div>
      </FormField>
    </FormGrid>
  );
}

const EMPTY_FORM = {
  server_id: '',
  name: '',
  ip: '',
  host: '',
  ssh_port: 22,
  ssh_username: 'root',
  ssh_password: '',
  ssh_private_key: '',
  ssh_key_passphrase: '',
  auth_type: 'password',
  emoji: '🖥️',
  color: '#01BCD6',
  display_order: 0,
  is_active: true,
};

export default function ServerMonitoring({ mode = 'server' }) {
  // mode: 'server' (시스템/프로세스) | 'database' (DB 전용 페이지)
  const isDbMode = mode === 'database';

  const [servers, setServers] = useState([]);          // [{server_id, name, ip, color, emoji, ...}]
  const [serversLoading, setServersLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [serverMetrics, setServerMetrics] = useState({});
  const [metricsHistory, setMetricsHistory] = useState({});
  const [pm2Status, setPm2Status] = useState({});
  const [logs, setLogs] = useState('');
  const [logServer, setLogServer] = useState(null);
  const [logProcess, setLogProcess] = useState('');
  const [logType, setLogType] = useState('error');
  const [logLines, setLogLines] = useState(50);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedSchema, setSelectedSchema] = useState('');
  const [isDumping, setIsDumping] = useState(false);

  // 서버 등록(레지스트리) 모달 상태
  const [showRegistry, setShowRegistry] = useState(false);
  const [editingId, setEditingId] = useState(null);    // null=목록, ''=신규, 'xxx'=편집
  const [form, setForm] = useState(EMPTY_FORM);
  const [savingServer, setSavingServer] = useState(false);
  const [testingSsh, setTestingSsh] = useState(false);
  const [registryQuery, setRegistryQuery] = useState('');
  const [monitorTab, setMonitorTab] = useState(isDbMode ? 'database' : 'system');  // 'system' | 'database' | 'process'
  const [processTab, setProcessTab] = useState('process'); // 'process' | 'logs'

  // 메트릭 이력 (추이 차트)
  const [history, setHistory] = useState([]);
  const [historyRange, setHistoryRange] = useState('7d'); // '1d' | '7d' | '30d'
  const [historyLoading, setHistoryLoading] = useState(false);
  const [networkMode, setNetworkMode] = useState('delta'); // 'cumulative' | 'delta'
  const [expandedChart, setExpandedChart] = useState(null); // null | 'cpu' | 'memory' | 'disk' | 'network'

  // 알람
  const [alerts, setAlerts] = useState([]);
  const [alertCounts, setAlertCounts] = useState({ active: 0, active_critical: 0, active_warning: 0, last_24h: 0 });
  const [showAllAlerts, setShowAllAlerts] = useState(false);

  // 프로세스 이벤트 타임라인
  const [procEvents, setProcEvents] = useState([]);
  const [procEventStats, setProcEventStats] = useState([]);

  // 로그 검색
  const [logSearch, setLogSearch] = useState('');

  // 접속자 통계 (서비스별)
  const [visitData, setVisitData] = useState([]);
  const [visitServices, setVisitServices] = useState([]);
  const [visitRange, setVisitRange] = useState('daily'); // daily/weekly/monthly

  // SLA 가동률
  const [uptime, setUptime] = useState([]);
  const [uptimeRange, setUptimeRange] = useState('7d');

  // SLA 시간별 추이 — activeTab(상단 서버 카드 선택)을 따라감
  const [uptimeTimeline, setUptimeTimeline] = useState([]);
  const [uptimeChartType, setUptimeChartType] = useState('line'); // 'line' | 'bar'

  // 운영 설정 모달 (룰 + 채널)
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState('rules'); // 'rules' | 'channels'
  const [alertRules, setAlertRules] = useState([]);
  const [channels, setChannels] = useState([]);
  const [editingRule, setEditingRule] = useState(null);
  const [editingChannel, setEditingChannel] = useState(null);

  // DB 통계 (DB 탭에서 사용)
  const [dbStats, setDbStats] = useState(null);
  const [dbStatsLoading, setDbStatsLoading] = useState(false);
  const [systemScan, setSystemScan] = useState(null);
  const [systemScanLoading, setSystemScanLoading] = useState(false);
  const [systemIncidents, setSystemIncidents] = useState([]);

  const schemaList = [
    { value: '', label: '-- 스키마 선택 (기본: antsnest) --' },
    { value: 'admin', label: '🔐 admin (SSO/인증)' },
    { value: 'antstalk', label: '💬 antstalk (앤츠톡)' },
    { value: 'antsnest', label: '🏢 antsnest (브랜드사이트)' },
    { value: 'crm', label: '📊 crm (비즈앤츠네스트)' },
    { value: 'edu', label: '📚 edu (에듀앤츠네스트)' },
    { value: 'store', label: '🛒 store (스토어)' },
    { value: 'story', label: '📖 story (스토리)' },
    { value: 'gantt', label: '📅 gantt (간트차트)' },
    { value: 'estimate', label: '💰 estimate (견적)' },
    { value: 'nodemaster', label: '🧠 nodemaster (노드마스터)' },
    { value: 'coin', label: '🪙 coin (앤츠코인)' },
    { value: 'market', label: '🏪 market (앤츠마켓)' },
    { value: 'skynet', label: '🤖 skynet (스카이넷)' },
  ];

  // 서버 목록 로드
  const fetchServers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/registry`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const list = (data.servers || []).filter(s => s.is_active);
        setServers(list);
        // activeTab/logServer 자동 선택 (현재 선택이 사라졌으면 첫 항목으로)
        setActiveTab(prev => list.find(s => s.server_id === prev) ? prev : list[0]?.server_id || null);
        setLogServer(prev => list.find(s => s.server_id === prev) ? prev : list[0]?.server_id || null);
      }
    } catch (e) {
      console.error('서버 목록 로드 실패:', e);
    } finally {
      setServersLoading(false);
    }
  }, []);

  const fetchSystemScan = useCallback(async () => {
    if (isDbMode) return;
    try {
      setSystemScanLoading(true);
      const res = await fetch('/api/admin/systems/scan', { credentials: 'include' });
      const data = await res.json();
      if (res.ok && data.success) setSystemScan(data);
    } catch (e) {
      console.error('시스템 스캔 로드 실패:', e);
    } finally {
      setSystemScanLoading(false);
    }
  }, [isDbMode]);

  const fetchSystemIncidents = useCallback(async () => {
    if (isDbMode) return;
    try {
      const res = await fetch('/api/admin/systems/incidents?status=open&limit=20', { credentials: 'include' });
      const data = await res.json();
      if (res.ok && data.success) setSystemIncidents(data.incidents || []);
    } catch (e) {
      console.error('시스템 incident 로드 실패:', e);
    }
  }, [isDbMode]);

  const updateSystemIncident = useCallback(async (incidentId, action) => {
    try {
      const res = await fetch(`/api/admin/systems/incidents/${encodeURIComponent(incidentId)}/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        alert(`incident 처리 실패: ${data.message || res.status}`);
        return;
      }
      fetchSystemIncidents();
    } catch (e) {
      alert(`incident 처리 오류: ${e.message}`);
    }
  }, [fetchSystemIncidents]);

  const restartIncidentProcess = useCallback(async (incidentId) => {
    if (!window.confirm('이 Incident의 PM2 프로세스를 재시작할까요? 실행 결과는 감사 로그에 기록됩니다.')) return;
    try {
      const res = await fetch(`/api/admin/systems/incidents/${encodeURIComponent(incidentId)}/restart`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        alert(`재시작 실패: ${data.message || res.status}`);
        return;
      }
      alert('재시작 명령을 실행했습니다. 프로세스 상태를 다시 확인하세요.');
      fetchSystemScan();
      fetchSystemIncidents();
    } catch (e) {
      alert(`재시작 오류: ${e.message}`);
    }
  }, [fetchSystemIncidents, fetchSystemScan]);

  const fetchAllMetrics = useCallback(async () => {
    if (servers.length === 0) return;
    const results = {};
    await Promise.all(servers.map(async (server) => {
      const id = server.server_id;
      try {
        const res = await fetch(`${API}/metrics/${id}`, { credentials: 'include' });
        if (res.ok) {
          results[id] = await res.json();
        } else {
          results[id] = { online: false, error: 'HTTP ' + res.status };
        }
      } catch (e) {
        results[id] = { online: false, error: e.message };
      }
    }));

    setServerMetrics(results);
    setLastUpdate(new Date());

    setMetricsHistory(prev => {
      const next = { ...prev };
      servers.forEach(s => {
        const id = s.server_id;
        if (results[id] && results[id].cpu) {
          next[id] = [...(prev[id] || []), results[id]].slice(-20);
        }
      });
      return next;
    });
  }, [servers]);

  const fetchDbStats = useCallback(async () => {
    setDbStatsLoading(true);
    try {
      const res = await fetch(`${API}/db/stats`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setDbStats(data);
      }
    } catch (e) {
      console.error('DB 통계 로드 실패:', e);
    } finally {
      setDbStatsLoading(false);
    }
  }, []);

  const fetchAllPm2 = useCallback(async () => {
    if (servers.length === 0) return;
    const results = {};
    await Promise.all(servers.map(async (server) => {
      const id = server.server_id;
      try {
        const res = await fetch(`${API}/pm2/${id}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          results[id] = data.processes || [];
        }
      } catch (e) {
        results[id] = [];
      }
    }));
    setPm2Status(results);
  }, [servers]);

  // ===== 서버 등록(레지스트리) =====
  const openRegistry = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setRegistryQuery('');
    setShowRegistry(true);
  };

  // 검색 필터링 (server_id / name / ip / host 매칭)
  const filteredServers = (() => {
    const q = registryQuery.trim().toLowerCase();
    if (!q) return servers;
    return servers.filter(s =>
      (s.server_id || '').toLowerCase().includes(q) ||
      (s.name || '').toLowerCase().includes(q) ||
      (s.ip || '').toLowerCase().includes(q) ||
      (s.host || '').toLowerCase().includes(q)
    );
  })();

  const openNewServer = () => {
    setForm({ ...EMPTY_FORM, display_order: servers.length + 1 });
    setEditingId('');
  };

  const openEditServer = (server) => {
    setForm({
      server_id: server.server_id,
      name: server.name,
      ip: server.ip,
      host: server.host,
      ssh_port: server.ssh_port,
      ssh_username: server.ssh_username,
      ssh_password: '',
      ssh_private_key: '',
      ssh_key_passphrase: '',
      auth_type: server.auth_type,
      emoji: server.emoji || '🖥️',
      color: server.color || '#01BCD6',
      display_order: server.display_order,
      is_active: server.is_active,
    });
    setEditingId(server.server_id);
  };

  const saveServer = async () => {
    if (!form.server_id || !form.name || !form.ip || !form.host) {
      alert('server_id / 이름 / IP / Host는 필수입니다.');
      return;
    }
    setSavingServer(true);
    try {
      const isEdit = !!editingId;
      const url = isEdit ? `${API}/registry/${editingId}` : `${API}/registry`;
      const method = isEdit ? 'PUT' : 'POST';
      const body = { ...form };
      // 쓰지 않는 인증 수단은 아예 보내지 않는다. 편집 중 빈 값은 "기존 것 유지"다.
      if (form.auth_type !== 'password') body.ssh_password = '';
      if (form.auth_type !== 'key') { body.ssh_private_key = ''; body.ssh_key_passphrase = ''; }
      if (isEdit && !body.ssh_password) delete body.ssh_password;
      if (isEdit && !body.ssh_private_key) { delete body.ssh_private_key; delete body.ssh_key_passphrase; }
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchServers();
        setEditingId(null);
        setForm(EMPTY_FORM);
      } else {
        alert(`저장 실패: ${data.message || res.status}`);
      }
    } catch (e) {
      alert(`저장 오류: ${e.message}`);
    } finally {
      setSavingServer(false);
    }
  };

  const deleteServer = async (serverId) => {
    if (!window.confirm(`'${serverId}' 서버를 삭제하시겠습니까?`)) return;
    try {
      const res = await fetch(`${API}/registry/${serverId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        await fetchServers();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(`삭제 실패: ${data.message || res.status}`);
      }
    } catch (e) {
      alert(`삭제 오류: ${e.message}`);
    }
  };

  const testSshConnection = async () => {
    if (form.auth_type === 'local') {
      alert('로컬 서버는 SSH 테스트가 불필요합니다.');
      return;
    }
    if (!form.host) {
      alert('Host를 입력하세요.');
      return;
    }
    // 신규 등록이면 자격증명이 폼에 있어야 한다. 편집이면 비워둔 채 저장된 값으로 테스트한다.
    const isNew = editingId === '';
    if (isNew && form.auth_type === 'key' && !form.ssh_private_key) {
      alert('개인키를 입력하세요.');
      return;
    }
    if (isNew && form.auth_type === 'password' && !form.ssh_password) {
      alert('SSH 비밀번호를 입력하세요.');
      return;
    }
    setTestingSsh(true);
    try {
      const res = await fetch(`${API}/registry/test`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host: form.host,
          ssh_port: form.ssh_port,
          ssh_username: form.ssh_username,
          ssh_password: form.ssh_password,
          ssh_private_key: form.ssh_private_key,
          ssh_key_passphrase: form.ssh_key_passphrase,
          server_id: editingId || form.server_id,
          auth_type: form.auth_type,
        }),
      });
      const data = await res.json();
      alert(data.message || (data.success ? '연결 성공' : '연결 실패'));
    } catch (e) {
      alert(`테스트 오류: ${e.message}`);
    } finally {
      setTestingSsh(false);
    }
  };

  const fetchLogs = useCallback(async () => {
    if (!activeTab) return;
    try {
      const params = new URLSearchParams({ type: logType, lines: logLines });
      if (logProcess) params.set('process', logProcess);
      const res = await fetch(`${API}/pm2/${activeTab}/logs?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const noisePatterns = [
          /^\d+\|.*\| middleware secure app/,
          /^\d+\|.*\| req\.protocol ==/,
          /^\d+\|.*\| protocol ==/,
          /^\d+\|.*\| 정상 요청/,
          /^\d+\|.*\| \?.*\?.*\?/,
          /^\s*$/,
        ];
        const filtered = (data.logs || '').split('\n')
          .filter(line => !noisePatterns.some(p => p.test(line)))
          .join('\n');
        setLogs(filtered || '로그가 없습니다.');
      }
    } catch (e) {
      setLogs('로그를 불러올 수 없습니다: ' + e.message);
    }
  }, [activeTab, logProcess, logType, logLines]);

  const handlePm2Action = async (serverId, processName, action) => {
    const actionLabel = action === 'restart' ? '재시작' : '중지';
    if (!window.confirm(`[${serverId}] ${processName}을(를) ${actionLabel}하시겠습니까?`)) return;

    setActionLoading(`${serverId}-${processName}-${action}`);
    try {
      const res = await fetch(`${API}/pm2/action`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ serverId, processName, action }),
      });
      const data = await res.json();
      if (data.success) {
        alert(`${processName} ${actionLabel} 완료`);
        setTimeout(() => fetchAllPm2(), 2000);
      } else {
        alert(`${actionLabel} 실패: ${data.message}`);
      }
    } catch (e) {
      alert(`오류: ${e.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDumpDownload = async () => {
    const schemaLabel = schemaList.find(s => s.value === selectedSchema)?.label || 'antsnest';
    if (!window.confirm(`[${schemaLabel}] 스키마의 데이터베이스 덤프를 생성하시겠습니까?\n\n⏱️ 데이터 크기에 따라 수 분이 걸릴 수 있습니다.`)) return;

    setIsDumping(true);
    try {
      const body = selectedSchema ? { schema: selectedSchema } : {};
      const response = await fetch(`${API}/pg_dump`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('덤프 생성 실패');

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'database_dump.sql';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      alert('덤프 파일이 다운로드되었습니다.');
    } catch (e) {
      alert('덤프 오류: ' + e.message);
    } finally {
      setIsDumping(false);
    }
  };

  // 최초: 서버 목록 로드
  useEffect(() => {
    fetchServers();
  }, [fetchServers]);

  useEffect(() => {
    if (isDbMode) return;
    fetchSystemScan();
    fetchSystemIncidents();
    const id = setInterval(() => {
      fetchSystemScan();
      fetchSystemIncidents();
    }, 30000);
    return () => clearInterval(id);
  }, [fetchSystemScan, fetchSystemIncidents, isDbMode]);

  // 서버 목록이 준비되면 메트릭/PM2 폴링 시작
  useEffect(() => {
    if (servers.length === 0) return;
    fetchAllMetrics();
    fetchAllPm2();
    const metricsInterval = setInterval(fetchAllMetrics, 30000);
    const pm2Interval = setInterval(fetchAllPm2, 30000);
    return () => {
      clearInterval(metricsInterval);
      clearInterval(pm2Interval);
    };
  }, [fetchAllMetrics, fetchAllPm2, servers.length]);

  useEffect(() => {
    if (activeTab && monitorTab === 'process') fetchLogs();
  }, [fetchLogs, activeTab, monitorTab]);

  // 서버 탭이 바뀌면 로그 프로세스 필터 초기화
  useEffect(() => {
    setLogProcess('');
  }, [activeTab]);

  // 메트릭 이력 로드 (프로세스 탭 진입 + range 변경 + activeTab 변경 시)
  const fetchHistory = useCallback(async () => {
    if (!activeTab) return;
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API}/metrics-history/${activeTab}?range=${historyRange}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.data || []);
      }
    } catch (e) {
      console.error('history fetch fail:', e);
    } finally {
      setHistoryLoading(false);
    }
  }, [activeTab, historyRange]);

  useEffect(() => {
    if ((monitorTab === 'system' || monitorTab === 'process') && activeTab) fetchHistory();
  }, [monitorTab, activeTab, historyRange, fetchHistory]);

  // 알람 폴링 (10초)
  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`/api/admin/alerts?status=${showAllAlerts ? 'all' : 'active'}&limit=50`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
        setAlertCounts(data.counts || {});
      }
    } catch (e) {}
  }, [showAllAlerts]);

  useEffect(() => {
    fetchAlerts();
    const id = setInterval(fetchAlerts, 10000);
    return () => clearInterval(id);
  }, [fetchAlerts]);

  const acknowledgeAlert = async (id) => {
    await fetch(`/api/admin/alerts/${id}/acknowledge`, { method: 'POST', credentials: 'include' });
    fetchAlerts();
  };
  const resolveAlert = async (id) => {
    await fetch(`/api/admin/alerts/${id}/resolve`, { method: 'POST', credentials: 'include' });
    fetchAlerts();
  };

  // SLA 가동률 폴링
  const fetchUptime = useCallback(async () => {
    try {
      const res = await fetch(`/api/admin/server/uptime?range=${uptimeRange}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUptime(data.servers || []);
      }
    } catch (e) {}
  }, [uptimeRange]);
  useEffect(() => {
    if (!isDbMode) {
      fetchUptime();
      const id = setInterval(fetchUptime, 60000);
      return () => clearInterval(id);
    }
  }, [fetchUptime, isDbMode]);

  // SLA timeline (activeTab + selected range)
  const fetchUptimeTimeline = useCallback(async () => {
    if (!activeTab) return;
    try {
      const res = await fetch(
        `/api/admin/server/uptime-timeline?server_id=${activeTab}&range=${uptimeRange}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        setUptimeTimeline(data.data || []);
      }
    } catch (e) {}
  }, [activeTab, uptimeRange]);
  useEffect(() => {
    if (!isDbMode) fetchUptimeTimeline();
  }, [fetchUptimeTimeline, isDbMode]);

  // 룰/채널 관리 데이터 fetch
  const fetchRules = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/alert-rules', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAlertRules(data.rules || []);
      }
    } catch (e) {}
  }, []);
  const fetchChannels = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/notification-channels', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setChannels(data.channels || []);
      }
    } catch (e) {}
  }, []);
  useEffect(() => {
    if (showSettings) { fetchRules(); fetchChannels(); }
  }, [showSettings, fetchRules, fetchChannels]);

  const saveRule = async (rule) => {
    const isNew = !rule.id;
    const url = isNew ? '/api/admin/alert-rules' : `/api/admin/alert-rules/${rule.id}`;
    const method = isNew ? 'POST' : 'PUT';
    const res = await fetch(url, {
      method, credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rule),
    });
    if (res.ok) { setEditingRule(null); fetchRules(); }
    else alert('저장 실패');
  };
  const deleteRule = async (id) => {
    if (!window.confirm('이 룰을 삭제하시겠습니까?')) return;
    await fetch(`/api/admin/alert-rules/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchRules();
  };
  const saveChannel = async (ch) => {
    const isNew = !ch.id;
    const url = isNew ? '/api/admin/notification-channels' : `/api/admin/notification-channels/${ch.id}`;
    const res = await fetch(url, {
      method: isNew ? 'POST' : 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ch),
    });
    if (res.ok) { setEditingChannel(null); fetchChannels(); }
    else alert('저장 실패');
  };
  const deleteChannel = async (id) => {
    if (!window.confirm('이 채널을 삭제하시겠습니까?')) return;
    await fetch(`/api/admin/notification-channels/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchChannels();
  };
  const testChannel = async (id) => {
    const res = await fetch(`/api/admin/notification-channels/${id}/test`, { method: 'POST', credentials: 'include' });
    const data = await res.json();
    alert(data.message || (data.success ? '✅ 발송 완료' : '❌ 실패'));
  };

  // 접속자 통계 (프로세스 탭 진입 시)
  const fetchVisits = useCallback(async () => {
    try {
      const res = await fetch(`/api/admin/visits?range=${visitRange}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setVisitData(data.data || []);
        setVisitServices(data.services || []);
      }
    } catch (e) {}
  }, [visitRange]);

  useEffect(() => {
    if (monitorTab === 'process' && !isDbMode) {
      fetchVisits();
      const id = setInterval(fetchVisits, 60000);
      return () => clearInterval(id);
    }
  }, [monitorTab, isDbMode, fetchVisits]);

  // 프로세스 이벤트 (프로세스 탭 진입 시)
  const fetchProcEvents = useCallback(async () => {
    if (!activeTab) return;
    try {
      const res = await fetch(`/api/admin/server/process-events?server_id=${activeTab}&range=7d&limit=80`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setProcEvents(data.events || []);
        setProcEventStats(data.stats || []);
      }
    } catch (e) {}
  }, [activeTab]);

  useEffect(() => {
    if (monitorTab === 'process' && activeTab) {
      fetchProcEvents();
      const id = setInterval(fetchProcEvents, 30000);
      return () => clearInterval(id);
    }
  }, [monitorTab, activeTab, fetchProcEvents]);

  // 데이터베이스 탭 진입 시 통계 로드 (30초 자동 갱신)
  useEffect(() => {
    if (monitorTab !== 'database') return;
    fetchDbStats();
    const id = setInterval(fetchDbStats, 30000);
    return () => clearInterval(id);
  }, [monitorTab, fetchDbStats]);

  const systemServices = systemScan?.services || [];
  const systemInstances = [
    ...(systemScan?.local?.instances || []),
    ...((systemScan?.pm2 || []).flatMap(item => item.processes || [])),
  ];
  const systemMatched = new Set(systemInstances.map(i => i.service_code).filter(Boolean)).size;
  const systemWarnings = systemInstances.filter(i => ['errored', 'stopped', 'error'].includes(i.status)).length;
  const systemUnknown = systemInstances.filter(i => !i.service_code && !['errored', 'stopped', 'error'].includes(i.status));

  const currentServer = servers.find(s => s.server_id === activeTab);
  const currentMetrics = activeTab ? serverMetrics[activeTab] : null;
  const currentHistory = (activeTab && metricsHistory[activeTab]) || [];
  const currentPm2 = (activeTab && pm2Status[activeTab]) || [];
  const currentColor = getServerColor(currentServer, servers.findIndex(s => s.server_id === activeTab));

  const cpuChartData = {
    labels: currentHistory.map((_, i) => {
      const secsAgo = (currentHistory.length - 1 - i) * 30;
      return secsAgo === 0 ? '지금' : `${secsAgo}초 전`;
    }),
    datasets: [{
      label: 'CPU %',
      data: currentHistory.map(m => parseFloat(m.cpu?.usage || 0)),
      borderColor: currentColor,
      backgroundColor: `${currentColor}20`,
      fill: true,
      tension: 0.3,
      pointRadius: 2,
    }],
  };

  const memoryChartData = currentMetrics?.memory ? {
    labels: ['사용 중', '여유'],
    datasets: [{
      data: [parseFloat(currentMetrics.memory.used), parseFloat(currentMetrics.memory.free)],
      backgroundColor: [currentColor, '#e9ecef'],
      borderWidth: 0,
    }],
  } : null;

  const diskChartData = currentMetrics?.disk ? {
    labels: ['사용됨', '여유'],
    datasets: [{
      data: [currentMetrics.disk.usagePercent, 100 - currentMetrics.disk.usagePercent],
      backgroundColor: [
        currentMetrics.disk.usagePercent > 80 ? colors.danger : colors.warning,
        'rgba(255,255,255,0.06)',
      ],
      borderWidth: 0,
    }],
  } : null;

  return (
    <DashboardContainer>
      {/* 인증서 만료 알림. 만료는 조용해서 누가 말해 주기 전까지 모른다 —
          실제로 374일 지난 것을 물고 있던 서버가 있었다. */}
      <CertBanner />

      {/* 활성 알람 배너 */}
      {alertCounts.active > 0 && (
        <div style={{
          marginBottom: 14,
          padding: '12px 16px',
          borderRadius: 12,
          background: alertCounts.active_critical > 0
            ? 'linear-gradient(180deg, rgba(248,113,113,0.18) 0%, rgba(248,113,113,0.08) 100%)'
            : 'linear-gradient(180deg, rgba(251,191,36,0.18) 0%, rgba(251,191,36,0.08) 100%)',
          border: `1px solid ${alertCounts.active_critical > 0 ? 'rgba(248,113,113,0.4)' : 'rgba(251,191,36,0.4)'}`,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '1.4rem' }}>{alertCounts.active_critical > 0 ? '🚨' : '⚠️'}</span>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: alertCounts.active_critical > 0 ? colors.dangerLight : colors.warning }}>
              활성 알람 {alertCounts.active}건
              {alertCounts.active_critical > 0 && ` · 🚨 위험 ${alertCounts.active_critical}건`}
              {alertCounts.active_warning > 0 && ` · ⚠️ 경고 ${alertCounts.active_warning}건`}
            </div>
            <div style={{ fontSize: '0.78rem', color: colors.textMuted, marginTop: 2 }}>
              {alerts.filter(a => a.status === 'active').slice(0, 2).map(a => `${a.server_id}: ${a.rule_name}`).join(' / ') || ''}
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setShowAllAlerts(s => !s); document.getElementById('alerts-section')?.scrollIntoView({ behavior: 'smooth' }); }}
            style={{
              padding: '7px 14px',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.18)',
              background: 'rgba(255,255,255,0.06)',
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >전체 보기</button>
        </div>
      )}

      <TopBar>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: '#fff', letterSpacing: '-0.3px' }}>
            🖥️ 서버 모니터링
          </h1>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
            앤츠네스트 인프라 {servers.length}대 서버 실시간 모니터링
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <RefreshInfo>
            <LiveDot />
            {lastUpdate ? `갱신: ${lastUpdate.toLocaleTimeString('ko-KR')}` : '로딩 중...'}
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>| 30초 자동</span>
          </RefreshInfo>
          <ManageBtn type="button" onClick={() => setShowSettings(true)}>
            🔔 알람·채널
          </ManageBtn>
          {!isDbMode && (
            <ManageBtn type="button" onClick={openRegistry}>
              ⚙️ 서버 등록 관리
            </ManageBtn>
          )}
        </div>
      </TopBar>

      {!isDbMode && (
        <SectionContainer style={{ marginBottom: 14 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 14,
            flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#fff', marginBottom: 4 }}>
                운영 빠른판
              </div>
              <div style={{ fontSize: '0.78rem', color: colors.textMuted }}>
                전체 시스템 확인, 프로세스 점검, 장애 알림, DB/백업 관리를 이 화면에서 바로 접근합니다.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                {[
                  ['등록 서비스', systemServices.length],
                  ['감지 서비스', systemMatched],
                  ['실행 인스턴스', systemInstances.length],
                  ['주의 프로세스', systemWarnings],
                  ['미매칭', systemUnknown.length],
                ].map(([label, value]) => (
                  <span key={label} style={{
                    display: 'inline-flex',
                    gap: 6,
                    alignItems: 'center',
                    padding: '6px 9px',
                    borderRadius: 9,
                    background: 'rgba(255,255,255,0.05)',
                    border: `1px solid ${label === '주의 프로세스' && value > 0 ? 'rgba(248,113,113,0.35)' : label === '미매칭' && value > 0 ? 'rgba(251,191,36,0.32)' : 'rgba(139,210,244,0.13)'}`,
                    color: label === '주의 프로세스' && value > 0 ? colors.dangerLight : label === '미매칭' && value > 0 ? colors.warning : '#fff',
                    fontSize: '0.76rem',
                    fontWeight: 700,
                  }}>
                    <span style={{ color: colors.textMuted, fontWeight: 600 }}>{label}</span>
                    <strong>{systemScanLoading && !systemScan ? '...' : value}</strong>
                  </span>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <ManageBtn type="button" onClick={() => { window.location.href = '/Admin/systems'; }}>
                전체 시스템 현황
              </ManageBtn>
              <ManageBtn type="button" onClick={() => setMonitorTab('process')}>
                프로세스 보기
              </ManageBtn>
              <ManageBtn
                type="button"
                onClick={() => {
                  setShowAllAlerts(true);
                  setTimeout(() => document.getElementById('alerts-section')?.scrollIntoView({ behavior: 'smooth' }), 50);
                }}
              >
                장애/알림 보기
              </ManageBtn>
              <ManageBtn type="button" onClick={() => { window.location.href = '/Admin/database'; }}>
                DB/백업 보기
              </ManageBtn>
            </div>
          </div>
        </SectionContainer>
      )}

      {!isDbMode && systemWarnings > 0 && (
        <SectionContainer style={{
          marginBottom: 14,
          borderColor: 'rgba(248,113,113,0.28)',
          boxShadow: '0 0 0 1px rgba(248,113,113,0.08), 0 14px 28px rgba(0,0,0,0.22)',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 10,
          }}>
            <div>
              <div style={{ fontSize: '0.95rem', fontWeight: 800, color: colors.dangerLight }}>
                주의 프로세스 {systemWarnings}개 감지
              </div>
              <div style={{ fontSize: '0.76rem', color: colors.textMuted, marginTop: 3 }}>
                stopped/errored/error 상태의 프로세스를 먼저 확인하세요. Phase2에서 이 항목이 incident로 자동 기록됩니다.
              </div>
            </div>
            <ManageBtn type="button" onClick={() => setMonitorTab('process')}>
              프로세스 탭에서 보기
            </ManageBtn>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 8,
          }}>
            {systemInstances
              .filter(i => ['errored', 'stopped', 'error'].includes(i.status))
              .slice(0, 8)
              .map((item, idx) => (
                <div key={`${item.server_id}-${item.process_name}-${item.pid}-${idx}`} style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: 'rgba(248,113,113,0.09)',
                  border: '1px solid rgba(248,113,113,0.22)',
                  color: '#fff',
                  minWidth: 0,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
                    <strong style={{ fontSize: '0.84rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.service_name || item.process_name || 'unknown process'}
                    </strong>
                    <span style={{
                      color: colors.dangerLight,
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      flex: '0 0 auto',
                    }}>
                      {item.status}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.server_id || 'unknown'} · {item.process_name || item.source || '-'} {item.port ? `· :${item.port}` : ''} {item.pid ? `· PID ${item.pid}` : ''}
                  </div>
                </div>
              ))}
          </div>
        </SectionContainer>
      )}

      {!isDbMode && systemUnknown.length > 0 && (
        <SectionContainer style={{
          marginBottom: 14,
          borderColor: 'rgba(251,191,36,0.22)',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 10,
          }}>
            <div>
              <div style={{ fontSize: '0.95rem', fontWeight: 800, color: colors.warning }}>
                미매칭 프로세스 {systemUnknown.length}개
              </div>
              <div style={{ fontSize: '0.76rem', color: colors.textMuted, marginTop: 3 }}>
                실행 중이지만 서비스 registry와 연결되지 않은 포트/프로세스입니다. 등록 누락 또는 임시 개발 서버일 수 있습니다.
              </div>
            </div>
            <ManageBtn type="button" onClick={() => { window.location.href = '/Admin/systems'; }}>
              시스템 현황에서 정리
            </ManageBtn>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 8,
          }}>
            {systemUnknown.slice(0, 10).map((item, idx) => (
              <div key={`unknown-${item.server_id}-${item.process_name}-${item.pid}-${item.port}-${idx}`} style={{
                padding: '10px 12px',
                borderRadius: 10,
                background: 'rgba(251,191,36,0.08)',
                border: '1px solid rgba(251,191,36,0.18)',
                color: '#fff',
                minWidth: 0,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
                  <strong style={{ fontSize: '0.84rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.process_name || item.source || 'unknown'}
                  </strong>
                  <span style={{
                    color: colors.warning,
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    flex: '0 0 auto',
                  }}>
                    미매칭
                  </span>
                </div>
                <div style={{ fontSize: '0.72rem', color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.server_id || 'unknown'} {item.port ? `· :${item.port}` : ''} {item.pid ? `· PID ${item.pid}` : ''} · {item.source || '-'}
                </div>
                {item.command_line && (
                  <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.42)', marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.command_line}
                  </div>
                )}
              </div>
            ))}
          </div>
        </SectionContainer>
      )}

      {!isDbMode && systemIncidents.length > 0 && (
        <SectionContainer style={{
          marginBottom: 14,
          borderColor: 'rgba(248,113,113,0.28)',
          background: 'linear-gradient(180deg, rgba(69,10,10,0.28), rgba(10,25,41,0.78))',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 10,
          }}>
            <div>
              <div style={{ fontSize: '0.95rem', fontWeight: 800, color: colors.dangerLight }}>
                열린 PM2 Incident {systemIncidents.length}건
              </div>
              <div style={{ fontSize: '0.76rem', color: colors.textMuted, marginTop: 3 }}>
                PM2 stopped/errored/restart 폭증이 감지되어 DB에 기록된 항목입니다. AntsTalk 알림은 전송되며, AI 원인 분석 루프는 다음 단계에서 붙입니다.
              </div>
            </div>
            <ManageBtn type="button" onClick={() => setMonitorTab('process')}>
              프로세스/로그 확인
            </ManageBtn>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 8,
          }}>
            {systemIncidents.slice(0, 8).map((item) => (
              <div key={item.incident_id} style={{
                padding: '10px 12px',
                borderRadius: 10,
                background: 'rgba(248,113,113,0.09)',
                border: '1px solid rgba(248,113,113,0.22)',
                color: '#fff',
                minWidth: 0,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
                  <strong style={{ fontSize: '0.84rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.process_name || item.service_code || item.server_id || item.incident_id}
                  </strong>
                  <span style={{ color: colors.dangerLight, fontSize: '0.72rem', fontWeight: 800, flex: '0 0 auto' }}>
                    {item.kind}
                  </span>
                </div>
                <div style={{ fontSize: '0.72rem', color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.server_id || 'unknown'} · {item.status} · {item.severity}
                </div>
                <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.42)', marginTop: 5 }}>
                  {item.opened_at ? new Date(item.opened_at).toLocaleString('ko-KR') : ''}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 9 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setMonitorTab('process');
                      setActiveTab(item.server_id || activeTab);
                      setLogProcess(item.process_name || '');
                    }}
                    style={{
                      border: '1px solid rgba(139,210,244,0.22)',
                      background: 'rgba(139,210,244,0.08)',
                      color: colors.primaryLight,
                      borderRadius: 7,
                      padding: '5px 8px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    로그확인
                  </button>
                  <button
                    type="button"
                    onClick={() => restartIncidentProcess(item.incident_id)}
                    style={{
                      border: '1px solid rgba(251,191,36,0.28)',
                      background: 'rgba(251,191,36,0.1)',
                      color: colors.warning,
                      borderRadius: 7,
                      padding: '5px 8px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    재시작
                  </button>
                  <button
                    type="button"
                    onClick={() => updateSystemIncident(item.incident_id, 'resolve')}
                    style={{
                      border: '1px solid rgba(34,197,94,0.24)',
                      background: 'rgba(34,197,94,0.1)',
                      color: colors.successLight,
                      borderRadius: 7,
                      padding: '5px 8px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    해결
                  </button>
                  <button
                    type="button"
                    onClick={() => updateSystemIncident(item.incident_id, 'ignore')}
                    style={{
                      border: '1px solid rgba(255,255,255,0.16)',
                      background: 'rgba(255,255,255,0.05)',
                      color: colors.textMuted,
                      borderRadius: 7,
                      padding: '5px 8px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    무시
                  </button>
                </div>
              </div>
            ))}
          </div>
        </SectionContainer>
      )}

      {serversLoading ? (
        <SectionContainer>
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: colors.textDim }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
            <div>서버 목록을 불러오는 중...</div>
          </div>
        </SectionContainer>
      ) : servers.length === 0 ? (
        <SectionContainer>
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: colors.textDim }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{isDbMode ? '🗄️' : '🖥️'}</div>
            <div style={{ marginBottom: '14px' }}>
              {isDbMode ? '등록된 서버가 없습니다. 서버 모니터링 페이지에서 먼저 SSH 서버를 등록해주세요.' : '등록된 서버가 없습니다.'}
            </div>
            {!isDbMode && (
              <PrimaryBtn type="button" onClick={() => { openRegistry(); openNewServer(); }}>
                + 서버 추가
              </PrimaryBtn>
            )}
          </div>
        </SectionContainer>
      ) : (
        <>
      {!isDbMode && (
      <MonitorTabBar style={{ marginBottom: 14 }}>
        <MonitorTab type="button" active={monitorTab === 'system'} onClick={() => setMonitorTab('system')}>
          📊 시스템 성능
        </MonitorTab>
        <MonitorTab type="button" active={monitorTab === 'process'} onClick={() => setMonitorTab('process')}>
          ⚙️ 프로세스
        </MonitorTab>
        {/* 헤츠너 클라우드 탭은 뺐다. Hetzner Cloud API 토큰이 있어야 하는데
            쓰지 않기로 했고, 서버는 이미 SSH(admin.servers · execOnServer)로
            다룬다 — IWINV 와 헤츠너를 같은 방식으로 본다. 화면 하나가 특정
            제공사 API 에만 매여 있을 이유가 없다. */}
      </MonitorTabBar>
      )}

      {!isDbMode && (
      <OverviewGrid>
        {servers.map((server, idx) => {
          const id = server.server_id;
          const m = serverMetrics[id];
          const isOnline = m && !m.error;
          const sc = getServerColor(server, idx);
          return (
            <ServerOverviewCard
              key={id}
              color={sc}
              active={activeTab === id}
              onClick={() => setActiveTab(id)}
            >
              <StatusBadge online={isOnline}>
                {isOnline ? '● 온라인' : '○ 오프라인'}
              </StatusBadge>
              <ServerName color={sc}>{server.emoji || '🖥️'} {server.name}</ServerName>
              <ServerIp>{server.ip}</ServerIp>
              {isOnline ? (
                <>
                  <MiniMetrics>
                    <MiniMetric>
                      <MiniLabel>CPU</MiniLabel>
                      <MiniValue warn={parseFloat(m.cpu?.usage) > 70} danger={parseFloat(m.cpu?.usage) > 90}>
                        {parseFloat(m.cpu?.usage).toFixed(1)}%
                      </MiniValue>
                    </MiniMetric>
                    <MiniMetric>
                      <MiniLabel>메모리</MiniLabel>
                      <MiniValue warn={parseFloat(m.memory?.usagePercent) > 70} danger={parseFloat(m.memory?.usagePercent) > 90}>
                        {parseFloat(m.memory?.usagePercent).toFixed(1)}%
                      </MiniValue>
                    </MiniMetric>
                    <MiniMetric>
                      <MiniLabel>디스크</MiniLabel>
                      <MiniValue warn={m.disk?.usagePercent > 70} danger={m.disk?.usagePercent > 90}>
                        {m.disk?.usagePercent}%
                      </MiniValue>
                    </MiniMetric>
                    <MiniMetric>
                      <MiniLabel>핑</MiniLabel>
                      <MiniValue
                        warn={m.ping_ms != null && m.ping_ms > 100}
                        danger={m.ping_ms === null}
                      >
                        {m.ping_ms === 0 ? '0ms' : m.ping_ms == null ? '—' : `${m.ping_ms.toFixed(0)}ms`}
                      </MiniValue>
                    </MiniMetric>
                  </MiniMetrics>
                  {(() => {
                    const role = getServerRole(server);
                    return (
                      <ServerCardFooter>
                        <span style={{ fontSize: '0.66rem', color: colors.textDim }}>
                          {m.network ? `↓${formatBytes(m.network.rx)} · ↑${formatBytes(m.network.tx)}` : ''}
                        </span>
                        <RolePill color={role.color}>
                          <span className="label">역할:</span> {role.label}
                        </RolePill>
                      </ServerCardFooter>
                    );
                  })()}
                </>
              ) : (
                <div style={{ color: colors.dangerLight, fontSize: '0.82rem' }}>
                  ⚠️ 연결 실패{m?.error ? `: ${m.error}` : ''}
                </div>
              )}
            </ServerOverviewCard>
          );
        })}
      </OverviewGrid>
      )}

      {((currentMetrics && !currentMetrics.error) || isDbMode) ? (
        <>
          {monitorTab === 'system' && (
          <>

          {/* 7일 추이 분석 */}
          <SectionContainer>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.05)', flexWrap: 'wrap', gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                📈 시스템 추이 분석 — {currentServer?.name || activeTab}
              </h2>
              <div style={{ display: 'flex', gap: 4, padding: 3, background: 'rgba(7,18,35,0.5)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10 }}>
                {[['1h_raw', '🔍 상세(1h)'], ['6h_raw', '🔍 상세(6h)'], ['1d', '시간(1일)'], ['7d', '시간(7일)'], ['30d', '일별(30일)'], ['1y', '월별(1년)']].map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setHistoryRange(v)}
                    style={{
                      padding: '5px 14px',
                      border: 0,
                      borderRadius: 7,
                      background: historyRange === v ? `${colors.primary}22` : 'transparent',
                      color: historyRange === v ? colors.primaryLight : colors.textMuted,
                      fontSize: '0.78rem',
                      fontWeight: historyRange === v ? 700 : 500,
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >{label}</button>
                ))}
              </div>
            </div>
            {historyLoading && history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: colors.textDim }}>로드 중...</div>
            ) : history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: colors.textDim, fontSize: '0.86rem' }}>
                <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>📊</div>
                아직 수집된 이력이 없습니다.<br/>
                <span style={{ fontSize: '0.74rem' }}>모니터링 페이지를 열어두면 1분마다 자동 수집됩니다.</span>
              </div>
            ) : (() => {
              const labels = history.map(p => {
                const d = new Date(p.bucket);
                if (historyRange === '1y') return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}`;
                if (historyRange === '30d') return `${d.getMonth() + 1}/${d.getDate()}`;
                if (historyRange === '7d') return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}시`;
                // 1d / 1h_raw / 6h_raw 모두 시:분 표시
                return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
              });
              const mkLine = (key, color, fillCol) => ({
                labels,
                datasets: [{
                  label: key,
                  data: history.map(p => p[key]),
                  borderColor: color,
                  backgroundColor: fillCol || `${color}22`,
                  fill: true,
                  tension: 0.3,
                  pointRadius: history.length > 50 ? 0 : 2,
                  borderWidth: 2,
                }],
              });
              const trendOpts = (max) => ({
                ...lineOptions,
                scales: {
                  ...lineOptions.scales,
                  y: { ...lineOptions.scales.y, max },
                },
              });
              // 네트워크: 누적 또는 구간 증가량
              const isDelta = networkMode === 'delta';
              const computeNet = (key) => history.map((p, i) => {
                const v = Number(p[key] || 0);
                if (!isDelta) return v / (1024 * 1024 * 1024); // GB 누적
                if (i === 0) return 0;
                const prev = Number(history[i - 1][key] || 0);
                const diff = Math.max(0, v - prev); // 카운터 리셋 대비
                return diff / (1024 * 1024); // MB per bucket
              });
              const netUnit = isDelta ? 'MB' : 'GB';
              const netData = {
                labels,
                datasets: [
                  {
                    label: `RX (${netUnit})`,
                    data: computeNet('network_rx'),
                    borderColor: '#60a5fa',
                    backgroundColor: 'rgba(96,165,250,0.15)',
                    fill: !isDelta,
                    tension: 0.3,
                    pointRadius: history.length > 50 ? 0 : 2,
                    borderWidth: 2,
                  },
                  {
                    label: `TX (${netUnit})`,
                    data: computeNet('network_tx'),
                    borderColor: '#f472b6',
                    backgroundColor: 'rgba(244,114,182,0.15)',
                    fill: !isDelta,
                    tension: 0.3,
                    pointRadius: history.length > 50 ? 0 : 2,
                    borderWidth: 2,
                  },
                ],
              };
              const netOpts = {
                ...lineOptions,
                scales: {
                  ...lineOptions.scales,
                  y: { beginAtZero: true, grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: 'rgba(255,255,255,0.4)', font: { size: 9 }, callback: (v) => v + ' ' + netUnit } },
                },
              };
              const cards = [
                { key: 'cpu', title: 'CPU %', data: mkLine('cpu', colors.primary), opts: trendOpts(100) },
                { key: 'memory', title: '메모리 %', data: mkLine('memory', '#34d399'), opts: trendOpts(100) },
                { key: 'disk', title: '디스크 %', data: mkLine('disk', '#fbbf24'), opts: trendOpts(100) },
              ];
              const cardStyle = (key) => ({
                cursor: 'pointer',
                gridColumn: expandedChart === key ? '1 / -1' : 'auto',
                height: expandedChart === key ? 460 : 260,
                display: expandedChart && expandedChart !== key ? 'none' : 'flex',
                transition: 'height 0.2s ease',
              });
              const ExpandHint = () => (
                <span
                  style={{ marginLeft: 8, color: colors.textDim, fontSize: '0.7rem', fontWeight: 500 }}
                  title="클릭하여 확대/축소"
                >
                  {expandedChart ? '⤡ 축소' : '⤢ 확대'}
                </span>
              );
              return (
                <ChartGrid>
                  {cards.map(c => (
                    <ChartCard
                      key={c.key}
                      style={cardStyle(c.key)}
                      onClick={() => setExpandedChart(expandedChart === c.key ? null : c.key)}
                    >
                      <ChartTitle>
                        {c.title}<ExpandHint />
                      </ChartTitle>
                      <ChartWrapper><Line data={c.data} options={c.opts} /></ChartWrapper>
                    </ChartCard>
                  ))}
                  <ChartCard
                    style={cardStyle('network')}
                    onClick={(e) => {
                      // 토글 버튼 클릭은 확대/축소 막기
                      if (e.target.closest('button')) return;
                      setExpandedChart(expandedChart === 'network' ? null : 'network');
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <ChartTitle style={{ marginBottom: 0 }}>
                        네트워크 {isDelta ? '구간 증가량' : '누적'} ({netUnit})
                        <ExpandHint />
                      </ChartTitle>
                      <div style={{ display: 'flex', gap: 2, padding: 2, background: 'rgba(7,18,35,0.5)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 8 }}>
                        <button
                          type="button"
                          onClick={() => setNetworkMode('delta')}
                          style={{
                            padding: '3px 9px', border: 0, borderRadius: 6,
                            background: networkMode === 'delta' ? `${colors.primary}22` : 'transparent',
                            color: networkMode === 'delta' ? colors.primaryLight : colors.textMuted,
                            fontSize: '0.68rem', fontWeight: networkMode === 'delta' ? 700 : 500,
                            fontFamily: 'inherit', cursor: 'pointer',
                          }}
                        >증가량</button>
                        <button
                          type="button"
                          onClick={() => setNetworkMode('cumulative')}
                          style={{
                            padding: '3px 9px', border: 0, borderRadius: 6,
                            background: networkMode === 'cumulative' ? `${colors.primary}22` : 'transparent',
                            color: networkMode === 'cumulative' ? colors.primaryLight : colors.textMuted,
                            fontSize: '0.68rem', fontWeight: networkMode === 'cumulative' ? 700 : 500,
                            fontFamily: 'inherit', cursor: 'pointer',
                          }}
                        >누적</button>
                      </div>
                    </div>
                    <ChartWrapper><Line data={netData} options={netOpts} /></ChartWrapper>
                  </ChartCard>
                </ChartGrid>
              );
            })()}
          </SectionContainer>
          <KpiGrid>
            <KpiCard accent={colors.primary}>
              <KpiIcon accent={colors.primary}>⚙️</KpiIcon>
              <KpiBody>
                <KpiLabel>CPU 사용률</KpiLabel>
                <KpiValue color={parseFloat(currentMetrics.cpu?.usage) > 90 ? colors.danger : parseFloat(currentMetrics.cpu?.usage) > 70 ? colors.warning : '#fff'}>
                  {parseFloat(currentMetrics.cpu?.usage).toFixed(1)}%
                </KpiValue>
                <KpiSub>{currentMetrics.cpu?.cores}코어</KpiSub>
              </KpiBody>
            </KpiCard>
            <KpiCard accent="#34d399">
              <KpiIcon accent="#34d399">🧠</KpiIcon>
              <KpiBody>
                <KpiLabel>메모리 사용률</KpiLabel>
                <KpiValue color={parseFloat(currentMetrics.memory?.usagePercent) > 90 ? colors.danger : parseFloat(currentMetrics.memory?.usagePercent) > 70 ? colors.warning : '#fff'}>
                  {parseFloat(currentMetrics.memory?.usagePercent).toFixed(1)}%
                </KpiValue>
                <KpiSub>{parseFloat(currentMetrics.memory?.used).toFixed(1)} / {parseFloat(currentMetrics.memory?.total).toFixed(1)} GB</KpiSub>
              </KpiBody>
            </KpiCard>
            <KpiCard accent="#fbbf24">
              <KpiIcon accent="#fbbf24">💾</KpiIcon>
              <KpiBody>
                <KpiLabel>디스크 사용률</KpiLabel>
                <KpiValue color={currentMetrics.disk?.usagePercent > 90 ? colors.danger : currentMetrics.disk?.usagePercent > 70 ? colors.warning : '#fff'}>
                  {currentMetrics.disk?.usagePercent}%
                </KpiValue>
                <KpiSub>{currentMetrics.disk?.used || '—'} / {currentMetrics.disk?.total || '—'}</KpiSub>
              </KpiBody>
            </KpiCard>
            <KpiCard accent="#a78bfa">
              <KpiIcon accent="#a78bfa">📈</KpiIcon>
              <KpiBody>
                <KpiLabel>로드 평균 (1m)</KpiLabel>
                <KpiValue color={parseFloat(currentMetrics.loadAvg?.['1m']) > currentMetrics.cpu?.cores ? colors.warning : '#fff'}>
                  {currentMetrics.loadAvg?.['1m'] || '—'}
                </KpiValue>
                <KpiSub>5m {currentMetrics.loadAvg?.['5m'] || '—'} · 15m {currentMetrics.loadAvg?.['15m'] || '—'}</KpiSub>
              </KpiBody>
            </KpiCard>
            <KpiCard accent="#60a5fa">
              <KpiIcon accent="#60a5fa">↓</KpiIcon>
              <KpiBody>
                <KpiLabel>네트워크 RX</KpiLabel>
                <KpiValue color="#60a5fa">
                  {formatBytes(currentMetrics.network?.rx)}
                </KpiValue>
                <KpiSub>누적 수신</KpiSub>
              </KpiBody>
            </KpiCard>
            <KpiCard accent="#f472b6">
              <KpiIcon accent="#f472b6">↑</KpiIcon>
              <KpiBody>
                <KpiLabel>네트워크 TX</KpiLabel>
                <KpiValue color="#f472b6">
                  {formatBytes(currentMetrics.network?.tx)}
                </KpiValue>
                <KpiSub>누적 송신</KpiSub>
              </KpiBody>
            </KpiCard>
            <KpiCard accent="#22c55e">
              <KpiIcon accent="#22c55e">🔗</KpiIcon>
              <KpiBody>
                <KpiLabel>TCP 연결</KpiLabel>
                <KpiValue color={colors.successLight}>
                  {currentMetrics.tcp?.established ?? '—'}
                </KpiValue>
                <KpiSub>established</KpiSub>
              </KpiBody>
            </KpiCard>
            <KpiCard accent="#94a3b8">
              <KpiIcon accent="#94a3b8">🧩</KpiIcon>
              <KpiBody>
                <KpiLabel>프로세스 수</KpiLabel>
                <KpiValue color="#fff">
                  {currentMetrics.processes?.total ?? '—'}
                </KpiValue>
                <KpiSub>OS 전체 (PID 기준)</KpiSub>
              </KpiBody>
            </KpiCard>
          </KpiGrid>

          <SectionContainer>
            <SectionTitle>📈 실시간 그래프</SectionTitle>
            <ChartGrid>
              <ChartCard>
                <ChartTitle>CPU 추이 (최근 10분)</ChartTitle>
                <ChartWrapper>
                  <Line data={cpuChartData} options={lineOptions} />
                </ChartWrapper>
              </ChartCard>

              {memoryChartData && (
                <ChartCard>
                  <ChartTitle>메모리 사용 현황</ChartTitle>
                  <ChartWrapper>
                    <Doughnut data={memoryChartData} options={doughnutOptions} />
                    <ChartCenter color={currentColor}>
                      <span className="big">{parseFloat(currentMetrics.memory.usagePercent).toFixed(1)}%</span>
                      <span className="small">{parseFloat(currentMetrics.memory.used).toFixed(1)} / {parseFloat(currentMetrics.memory.total).toFixed(1)} GB</span>
                    </ChartCenter>
                  </ChartWrapper>
                </ChartCard>
              )}

              {diskChartData && (
                <ChartCard>
                  <ChartTitle>디스크 사용 현황</ChartTitle>
                  <ChartWrapper>
                    <Doughnut data={diskChartData} options={doughnutOptions} />
                    <ChartCenter color={currentMetrics.disk.usagePercent > 80 ? colors.danger : '#fbbf24'}>
                      <span className="big">{currentMetrics.disk.usagePercent}%</span>
                      <span className="small">{currentMetrics.disk?.used || '—'} / {currentMetrics.disk?.total || '—'}</span>
                    </ChartCenter>
                  </ChartWrapper>
                </ChartCard>
              )}
            </ChartGrid>
          </SectionContainer>

          <SectionContainer>
            <SectionTitle>🔧 호스트 상세 정보</SectionTitle>
            <DetailGrid>
              <DetailItem>
                <span className="k">호스트명</span>
                <span className="v mono">{currentMetrics.hostname || '—'}</span>
              </DetailItem>
              <DetailItem>
                <span className="k">CPU 모델</span>
                <span className="v" style={{ fontSize: '0.84rem' }}>{currentMetrics.cpu?.model || '—'}</span>
              </DetailItem>
              <DetailItem>
                <span className="k">플랫폼 / 아키텍처</span>
                <span className="v mono">
                  {currentMetrics.platform || 'linux'} · {currentMetrics.arch || 'x64'}
                </span>
              </DetailItem>
              {currentMetrics.release && (
                <DetailItem>
                  <span className="k">커널</span>
                  <span className="v mono">{currentMetrics.release}</span>
                </DetailItem>
              )}
              <DetailItem>
                <span className="k">시스템 업타임</span>
                <span className="v">
                  {currentMetrics.uptime >= 24
                    ? `${Math.floor(currentMetrics.uptime / 24)}일 ${currentMetrics.uptime % 24}시간`
                    : `${currentMetrics.uptime}시간`}
                </span>
              </DetailItem>
              <DetailItem>
                <span className="k">메모리 (used / total)</span>
                <span className="v mono">
                  {parseFloat(currentMetrics.memory?.used).toFixed(2)} GB / {parseFloat(currentMetrics.memory?.total).toFixed(2)} GB
                </span>
              </DetailItem>
              <DetailItem>
                <span className="k">디스크 (used / total)</span>
                <span className="v mono">
                  {currentMetrics.disk?.used || '—'} / {currentMetrics.disk?.total || '—'}
                </span>
              </DetailItem>
              {currentMetrics.database && (
                <DetailItem>
                  <span className="k">App DB 연결 풀</span>
                  <span className="v">
                    {currentMetrics.database.totalCount}개{' '}
                    <span style={{ color: colors.textDim, fontWeight: 500, fontSize: '0.8rem' }}>
                      (유휴 {currentMetrics.database.idleCount})
                    </span>
                  </span>
                </DetailItem>
              )}
            </DetailGrid>
          </SectionContainer>

          </>
          )}

          {monitorTab === 'database' && (
          <>
          {/* DB 서버 선택 (앱 서버와 별개의 관리형 PostgreSQL 인스턴스) */}
          <SectionContainer>
            <SectionTitle>🗃️ DB 서버 선택 <span style={{ marginLeft: '8px', fontSize: '0.7rem', color: colors.textDim, fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>앱 서버와 별개로 운영되는 관리형 PostgreSQL 인스턴스</span></SectionTitle>
            <DbServerScroller>
              <DbServerCard active accent={colors.primary}>
                <DbServerHead accent={colors.primary}>
                  <span className="icon">🗄️</span>
                  <span className="name">{dbStats?.database?.name || 'antsnest_prd'}</span>
                  <Badge>온라인</Badge>
                  <DbRoleBadge>PRIMARY</DbRoleBadge>
                </DbServerHead>
                <div style={{ fontFamily: 'Fira Code, monospace', color: colors.textDim, fontSize: '0.74rem', marginBottom: '10px' }}>
                  antsnest.sldb.iwinv.net:5432 · iwinv 관리형
                </div>
                <DbServerMini>
                  <div className="col">
                    <div className="l">활성 연결</div>
                    <div className="v">{dbStats?.connections?.active ?? '—'}</div>
                  </div>
                  <div className="col">
                    <div className="l">평균 응답</div>
                    <div className="v">{dbStats?.avgResponseMs ? <>{dbStats.avgResponseMs}<span className="u">ms</span></> : '—'}</div>
                  </div>
                  <div className="col">
                    <div className="l">DB 크기</div>
                    <div className="v" style={{ fontSize: '0.78rem' }}>{dbStats?.database?.size_pretty || '—'}</div>
                  </div>
                  <div className="col">
                    <div className="l">복제 지연</div>
                    <div className="v">{(dbStats?.replication?.length || 0) === 0 ? <>0<span className="u">ms</span></> : <>—</>}</div>
                  </div>
                </DbServerMini>
              </DbServerCard>
            </DbServerScroller>
          </SectionContainer>

          <KpiGrid>
            <KpiCard accent={colors.primary}>
              <KpiIcon accent={colors.primary}>🗄️</KpiIcon>
              <KpiBody>
                <KpiLabel>데이터베이스 크기</KpiLabel>
                <KpiValue color={colors.primaryLight}>{dbStats?.database?.size_pretty || '…'}</KpiValue>
                <KpiSub>{dbStats?.database?.name || 'antsnest_prd'}</KpiSub>
              </KpiBody>
            </KpiCard>
            <KpiCard accent="#34d399">
              <KpiIcon accent="#34d399">👥</KpiIcon>
              <KpiBody>
                <KpiLabel>활성 연결</KpiLabel>
                <KpiValue color="#fff">{dbStats?.connections?.active ?? '…'}</KpiValue>
                <KpiSub>최대 {dbStats?.connections?.max_connections ?? '…'}</KpiSub>
              </KpiBody>
            </KpiCard>
            <KpiCard accent="#a78bfa">
              <KpiIcon accent="#a78bfa">⏱️</KpiIcon>
              <KpiBody>
                <KpiLabel>평균 응답 시간</KpiLabel>
                <KpiValue color="#fff">
                  {dbStats?.avgResponseMs ? <>{dbStats.avgResponseMs}<span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}> ms</span></> : '—'}
                </KpiValue>
                <KpiSub>최근 슬로 쿼리 평균</KpiSub>
              </KpiBody>
            </KpiCard>
            <KpiCard accent="#fbbf24">
              <KpiIcon accent="#fbbf24">📈</KpiIcon>
              <KpiBody>
                <KpiLabel>느린 쿼리</KpiLabel>
                <KpiValue color={(dbStats?.slowQueries?.length || 0) > 0 ? colors.warning : '#fff'}>
                  {dbStats?.slowQueriesAvailable ? (dbStats?.slowQueries?.length ?? 0) : '—'}
                </KpiValue>
                <KpiSub>{dbStats?.slowQueriesAvailable ? 'pg_stat_statements 기반' : '확장 미설치'}</KpiSub>
              </KpiBody>
            </KpiCard>
            <KpiCard accent="#22c55e">
              <KpiIcon accent="#22c55e">🔄</KpiIcon>
              <KpiBody>
                <KpiLabel>복제 지연</KpiLabel>
                <KpiValue color={colors.successLight}>
                  {(dbStats?.replication?.length || 0) === 0 ? <>0<span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}> ms</span></> : '—'}
                </KpiValue>
                <KpiSub>{(dbStats?.replication?.length || 0) === 0 ? '복제 없음' : `${dbStats.replication.length}개 노드`}</KpiSub>
              </KpiBody>
            </KpiCard>
            <KpiCard accent="#f472b6">
              <KpiIcon accent="#f472b6">🎯</KpiIcon>
              <KpiBody>
                <KpiLabel>캐시 적중률</KpiLabel>
                <KpiValue color={
                  dbStats?.aggregate?.cacheHitRatio == null ? '#fff'
                    : parseFloat(dbStats.aggregate.cacheHitRatio) >= 99 ? colors.successLight
                    : parseFloat(dbStats.aggregate.cacheHitRatio) >= 95 ? '#fff'
                    : colors.warning
                }>
                  {dbStats?.aggregate?.cacheHitRatio == null ? '—' : `${dbStats.aggregate.cacheHitRatio}%`}
                </KpiValue>
                <KpiSub>blks_hit / total reads</KpiSub>
              </KpiBody>
            </KpiCard>
          </KpiGrid>

          {/* 3단 그리드: 노드 트리 / 레플리케이션 / 주요 DB */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14, marginBottom: 14 }}>
            <SectionContainer style={{ margin: 0 }}>
              <SectionTitle>🗂️ 데이터베이스 노드 트리 <span style={{ marginLeft: '6px', color: colors.textDim, fontWeight: 500, fontSize: '0.7rem', textTransform: 'none' }}>{dbStats?.database?.name || 'antsnest_prd'}</span></SectionTitle>
              <TreeContainer>
                <TreeNode level={0} accent={colors.primary}>
                  <span className="ico">🗄️</span>
                  <span className="name">{dbStats?.database?.name || 'antsnest_prd'}</span>
                  <DbRoleBadge>PRIMARY</DbRoleBadge>
                  <span className="addr" style={{ marginLeft: 'auto' }}>antsnest.sldb.iwinv.net:5432</span>
                </TreeNode>
                {dbStats?.replication?.length > 0 ? (
                  dbStats.replication.map((r, i) => (
                    <TreeNode key={i} level={1} accent="#a78bfa">
                      <span className="ico">📋</span>
                      <span className="name">{r.application_name || `replica-${i + 1}`}</span>
                      <DbRoleBadge bg="rgba(167,139,250,0.18)" color="#c4b5fd" border="rgba(167,139,250,0.35)">REPLICA</DbRoleBadge>
                      <span className="addr" style={{ marginLeft: 'auto' }}>{r.client_addr || '—'}</span>
                      <span className="lag">
                        <Badge bg="rgba(34,197,94,0.16)" color={colors.successLight} border="rgba(34,197,94,0.3)">{r.state}</Badge>
                        <span className="v">지연 {r.replay_lag}</span>
                      </span>
                    </TreeNode>
                  ))
                ) : (
                  <div style={{ color: colors.textDim, fontSize: '0.78rem', padding: '12px', marginLeft: '24px', borderLeft: '2px dashed rgba(255,255,255,0.08)', paddingLeft: '14px' }}>
                    복제 노드 없음 — 단일 인스턴스 운영 중
                  </div>
                )}
              </TreeContainer>
            </SectionContainer>

            <SectionContainer style={{ margin: 0 }}>
              <SectionTitle>🔁 레플리케이션 상태</SectionTitle>
              {dbStats?.replication?.length > 0 ? (
                <TableContainer>
                  <StyledTable>
                    <thead>
                      <tr>
                        <th>역할</th>
                        <th>노드</th>
                        <th>상태</th>
                        <th>복제 지연</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><DbRoleBadge>PRIMARY</DbRoleBadge></td>
                        <td>{dbStats.database.name}</td>
                        <td><Badge>정상</Badge></td>
                        <td>—</td>
                      </tr>
                      {dbStats.replication.map((r, i) => (
                        <tr key={i}>
                          <td><DbRoleBadge bg="rgba(167,139,250,0.18)" color="#c4b5fd" border="rgba(167,139,250,0.35)">{r.sync_state === 'sync' ? 'SYNC' : 'REPLICA'}</DbRoleBadge></td>
                          <td>{r.application_name || `replica-${i + 1}`}</td>
                          <td><Badge>{r.state}</Badge></td>
                          <td>{r.replay_lag}</td>
                        </tr>
                      ))}
                    </tbody>
                  </StyledTable>
                </TableContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: '32px 16px', color: colors.textDim }}>
                  <div style={{ fontSize: '1.6rem', marginBottom: '8px' }}>🔁</div>
                  <div style={{ fontSize: '0.86rem', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>복제 설정 없음</div>
                  <div style={{ fontSize: '0.74rem' }}>현재 단일 PostgreSQL 인스턴스로 운영 중입니다.</div>
                </div>
              )}
            </SectionContainer>

            <SectionContainer style={{ margin: 0 }}>
              <SectionTitle>📚 주요 데이터베이스 상태</SectionTitle>
              {dbStats?.databases?.length > 0 ? (
                <TableContainer>
                  <StyledTable>
                    <thead>
                      <tr>
                        <th>데이터베이스</th>
                        <th>엔진</th>
                        <th style={{ textAlign: 'right' }}>활성 연결</th>
                        <th style={{ textAlign: 'right' }}>크기</th>
                        <th>상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dbStats.databases.map(d => (
                        <tr key={d.name}>
                          <td style={{ fontFamily: 'Fira Code, monospace', fontSize: '0.8rem', color: '#fff', fontWeight: 600 }}>{d.name}</td>
                          <td style={{ color: colors.textMuted, fontSize: '0.78rem' }}>PostgreSQL {dbStats.version}</td>
                          <td style={{ textAlign: 'right', color: '#fff', fontWeight: 600 }}>{d.active_connections}</td>
                          <td style={{ textAlign: 'right', color: colors.primaryLight, fontWeight: 600 }}>{d.size}</td>
                          <td><Badge>정상</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </StyledTable>
                </TableContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: colors.textDim }}>로드 중...</div>
              )}
            </SectionContainer>
          </div>

          {/* 슬로 쿼리 + 리소스 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 14, marginBottom: 14 }}>
            <SectionContainer style={{ margin: 0 }}>
              <SectionTitle>🐌 느린 쿼리 TOP 5</SectionTitle>
              {!dbStats?.slowQueriesAvailable ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: colors.textDim }}>
                  <div style={{ fontSize: '1.6rem', marginBottom: '8px' }}>🐌</div>
                  <div style={{ fontSize: '0.86rem', color: 'rgba(255,255,255,0.7)' }}>pg_stat_statements 확장이 활성화되지 않았습니다.</div>
                  <div style={{ fontSize: '0.74rem', marginTop: '6px' }}>활성화 후 슬로 쿼리 분석을 사용할 수 있습니다.</div>
                </div>
              ) : dbStats.slowQueries.length > 0 ? (
                <TableContainer>
                  <StyledTable>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>쿼리</th>
                        <th style={{ textAlign: 'right' }}>평균(ms)</th>
                        <th style={{ textAlign: 'right' }}>실행 횟수</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dbStats.slowQueries.map((q, i) => (
                        <tr key={i}>
                          <td style={{ color: colors.textDim }}>{i + 1}</td>
                          <td style={{ fontFamily: 'Fira Code, monospace', fontSize: '0.74rem', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={q.query}>{q.query}</td>
                          <td style={{ textAlign: 'right', color: colors.warning, fontWeight: 700 }}>{q.mean_ms}</td>
                          <td style={{ textAlign: 'right', color: colors.textMuted }}>{Number(q.calls).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </StyledTable>
                </TableContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: colors.textDim }}>✅ 느린 쿼리가 감지되지 않았습니다.</div>
              )}
            </SectionContainer>

            <SectionContainer style={{ margin: 0 }}>
              <SectionTitle>📊 DB 처리 통계 <span style={{ marginLeft: '6px', color: colors.textDim, fontWeight: 500, fontSize: '0.7rem', textTransform: 'none' }}>pg_stat_database 누적</span></SectionTitle>
              <ResourceGrid>
                <ResourceItem
                  color="#22c55e"
                  percent={dbStats?.aggregate?.cacheHitRatio || 0}
                >
                  <span className="lbl">캐시 적중률</span>
                  <span className="val">{dbStats?.aggregate?.cacheHitRatio == null ? '—' : `${dbStats.aggregate.cacheHitRatio}%`}</span>
                  <span className="bar"><span /></span>
                  <span className="sub">99%↑ 권장</span>
                </ResourceItem>
                <ResourceItem
                  color={colors.primary}
                  percent={dbStats?.aggregate ? Math.min(100, (dbStats.aggregate.commits / Math.max(1, dbStats.aggregate.commits + dbStats.aggregate.rollbacks)) * 100) : 0}
                >
                  <span className="lbl">트랜잭션 (커밋)</span>
                  <span className="val">{dbStats?.aggregate ? dbStats.aggregate.commits.toLocaleString() : '—'}</span>
                  <span className="bar"><span /></span>
                  <span className="sub">롤백 {dbStats?.aggregate ? dbStats.aggregate.rollbacks.toLocaleString() : '—'}</span>
                </ResourceItem>
                <ResourceItem
                  color="#a78bfa"
                  percent={dbStats?.aggregate?.rows_io ? 100 : 0}
                >
                  <span className="lbl">행 I/O 누적</span>
                  <span className="val" style={{ fontSize: '1.05rem' }}>{dbStats?.aggregate ? dbStats.aggregate.rows_io.toLocaleString() : '—'}</span>
                  <span className="bar"><span /></span>
                  <span className="sub">조회 + 페치</span>
                </ResourceItem>
                <ResourceItem
                  color={(dbStats?.aggregate?.deadlocks || 0) > 0 ? colors.danger : '#94a3b8'}
                  percent={(dbStats?.aggregate?.deadlocks || 0) > 0 ? 100 : 0}
                >
                  <span className="lbl">데드락 누적</span>
                  <span className="val" style={{ color: (dbStats?.aggregate?.deadlocks || 0) > 0 ? colors.danger : '#fff' }}>
                    {dbStats?.aggregate?.deadlocks ?? '—'}
                  </span>
                  <span className="bar"><span /></span>
                  <span className="sub">임시파일 {dbStats?.aggregate ? dbStats.aggregate.temp_files.toLocaleString() : '—'}개 · {formatBytes(dbStats?.aggregate?.temp_bytes || 0)}</span>
                </ResourceItem>
              </ResourceGrid>
            </SectionContainer>
          </div>

          {/* 상위 테이블 + 활성 쿼리 (기존 유지, 다른 디자인으로) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 14, marginBottom: 14 }}>
            <SectionContainer style={{ margin: 0 }}>
              <SectionTitle>📦 상위 테이블 TOP 10</SectionTitle>
              {dbStats?.topTables?.length > 0 ? (
                <TableContainer>
                  <StyledTable>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>테이블</th>
                        <th style={{ textAlign: 'right' }}>크기</th>
                        <th style={{ textAlign: 'right' }}>행 수</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dbStats.topTables.map((t, i) => (
                        <tr key={t.name}>
                          <td style={{ color: colors.textDim }}>{i + 1}</td>
                          <td style={{ fontFamily: 'Fira Code, monospace', fontSize: '0.78rem' }}>{t.name}</td>
                          <td style={{ textAlign: 'right', color: colors.primaryLight, fontWeight: 600 }}>{t.size}</td>
                          <td style={{ textAlign: 'right', color: colors.textMuted }}>{Number(t.rows).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </StyledTable>
                </TableContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: colors.textDim }}>로드 중...</div>
              )}
            </SectionContainer>

            <SectionContainer style={{ margin: 0 }}>
              <SectionTitle>⚡ 현재 활성 쿼리 ({dbStats?.activity?.length || 0})</SectionTitle>
              {dbStats?.activity?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '320px', overflowY: 'auto' }}>
                  {dbStats.activity.slice(0, 8).map(q => (
                    <DbStatBlock key={q.pid}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap', gap: '6px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <Badge>{q.state}</Badge>
                          <span style={{ color: colors.textDim, fontSize: '0.74rem', fontFamily: 'Fira Code, monospace' }}>
                            PID {q.pid} · {q.usename || '—'}
                          </span>
                        </div>
                        <span style={{ color: q.duration_sec > 5 ? colors.warning : colors.textDim, fontSize: '0.74rem', fontWeight: 600 }}>
                          {q.duration_sec >= 60 ? `${Math.floor(q.duration_sec / 60)}m ${q.duration_sec % 60}s` : `${q.duration_sec}s`}
                        </span>
                      </div>
                      <code style={{
                        display: 'block',
                        fontSize: '0.72rem',
                        color: 'rgba(255,255,255,0.78)',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        fontFamily: 'Fira Code, monospace',
                        lineHeight: 1.5,
                      }}>{q.query || '(쿼리 없음)'}</code>
                    </DbStatBlock>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: colors.textDim }}>✅ 활성 쿼리 없음</div>
              )}
            </SectionContainer>
          </div>

          <SectionContainer>
            <SectionTitle>📥 PostgreSQL 덤프</SectionTitle>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 280px', fontSize: '0.82rem', color: colors.textMuted, lineHeight: 1.55 }}>
                스키마 단위로 SQL 덤프를 생성·다운로드합니다. 데이터 크기에 따라 수 분 걸릴 수 있습니다.
              </div>
              <DumpSection>
                <Select
                  value={selectedSchema}
                  onChange={e => setSelectedSchema(e.target.value)}
                  style={{ minWidth: '220px', padding: '8px 10px' }}
                >
                  {schemaList.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </Select>
                <DumpButton onClick={handleDumpDownload} disabled={isDumping}>
                  {isDumping ? '⏳ 생성 중...' : '📥 다운로드'}
                </DumpButton>
              </DumpSection>
            </div>
          </SectionContainer>
          </>
          )}

          {monitorTab === 'process' && (
          <>

          {(() => {
            const onlineCount = currentPm2.filter(p => p.status === 'online').length;
            const erroredCount = currentPm2.filter(p => p.status === 'errored' || p.status === 'stopped').length;
            const totalCpu = currentPm2.reduce((s, p) => s + (parseFloat(p.cpu) || 0), 0);
            const totalMem = currentPm2.reduce((s, p) => s + (parseFloat(p.memory) || 0), 0);
            const totalRestarts = currentPm2.reduce((s, p) => s + (p.restarts || 0), 0);
            const flaky = currentPm2.filter(p => p.restarts > 50).length;
            return (
              <KpiGrid>
                <KpiCard accent={colors.primary}>
                  <KpiIcon accent={colors.primary}>📦</KpiIcon>
                  <KpiBody>
                    <KpiLabel>전체 프로세스</KpiLabel>
                    <KpiValue color="#fff">{currentPm2.length}</KpiValue>
                    <KpiSub>PM2 등록</KpiSub>
                  </KpiBody>
                </KpiCard>
                <KpiCard accent="#22c55e">
                  <KpiIcon accent="#22c55e">🟢</KpiIcon>
                  <KpiBody>
                    <KpiLabel>온라인</KpiLabel>
                    <KpiValue color={colors.successLight}>{onlineCount}</KpiValue>
                    <KpiSub>{currentPm2.length > 0 ? Math.round((onlineCount / currentPm2.length) * 100) : 0}% 정상</KpiSub>
                  </KpiBody>
                </KpiCard>
                <KpiCard accent="#f87171">
                  <KpiIcon accent="#f87171">🔴</KpiIcon>
                  <KpiBody>
                    <KpiLabel>오프라인 / 오류</KpiLabel>
                    <KpiValue color={erroredCount > 0 ? colors.danger : '#fff'}>{erroredCount}</KpiValue>
                    <KpiSub>주의 필요</KpiSub>
                  </KpiBody>
                </KpiCard>
                <KpiCard accent="#fbbf24">
                  <KpiIcon accent="#fbbf24">⚠️</KpiIcon>
                  <KpiBody>
                    <KpiLabel>플랩 (재시작 50+)</KpiLabel>
                    <KpiValue color={flaky > 0 ? colors.warning : '#fff'}>{flaky}</KpiValue>
                    <KpiSub>총 재시작 {totalRestarts.toLocaleString()}회</KpiSub>
                  </KpiBody>
                </KpiCard>
                <KpiCard accent="#a78bfa">
                  <KpiIcon accent="#a78bfa">⚡</KpiIcon>
                  <KpiBody>
                    <KpiLabel>합산 CPU</KpiLabel>
                    <KpiValue color={totalCpu > 100 ? colors.warning : '#fff'}>{totalCpu.toFixed(1)}%</KpiValue>
                    <KpiSub>모든 프로세스</KpiSub>
                  </KpiBody>
                </KpiCard>
                <KpiCard accent="#34d399">
                  <KpiIcon accent="#34d399">🧠</KpiIcon>
                  <KpiBody>
                    <KpiLabel>합산 메모리</KpiLabel>
                    <KpiValue color="#fff">{totalMem >= 1024 ? `${(totalMem/1024).toFixed(2)} GB` : `${totalMem.toFixed(0)} MB`}</KpiValue>
                    <KpiSub>모든 프로세스</KpiSub>
                  </KpiBody>
                </KpiCard>
              </KpiGrid>
            );
          })()}

          <SectionContainer>
            <SectionTitle>
              ⚙️ PM2 프로세스 — {currentServer?.name || activeTab}
              {currentPm2.length > 0 && (
                <span style={{
                  marginLeft: '8px',
                  padding: '2px 9px',
                  borderRadius: '999px',
                  background: 'rgba(1, 188, 214, 0.16)',
                  color: colors.primaryLight,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                }}>
                  {currentPm2.length}
                </span>
              )}
            </SectionTitle>

            {currentPm2.length > 0 ? (
              <TableContainer>
                <StyledTable>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>프로세스명</th>
                      <th>상태</th>
                      <th>PID</th>
                      <th>CPU</th>
                      <th>메모리</th>
                      <th>업타임</th>
                      <th>재시작</th>
                      <th>액션</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPm2.map((proc, idx) => (
                      <tr key={idx}>
                        <td style={{ color: colors.textDim }}>{proc.pm_id}</td>
                        <td style={{ fontWeight: 600 }}>{proc.name}</td>
                        <td>
                          <StatusDot status={proc.status} />
                          <span style={{
                            color: proc.status === 'online' ? colors.successLight : colors.dangerLight,
                            fontWeight: 600,
                          }}>
                            {proc.status}
                          </span>
                        </td>
                        <td style={{ color: colors.textDim }}>{proc.pid}</td>
                        <td>{proc.cpu}%</td>
                        <td>{proc.memory} MB</td>
                        <td>{proc.uptime >= 60 ? `${Math.floor(proc.uptime / 60)}h ${proc.uptime % 60}m` : `${proc.uptime}m`}</td>
                        <td style={{
                          color: proc.restarts > 50 ? colors.danger : proc.restarts > 10 ? colors.warning : 'inherit',
                          fontWeight: proc.restarts > 50 ? 700 : 'normal',
                        }}>
                          {proc.restarts > 50 ? '⚠️ ' : ''}{proc.restarts}
                        </td>
                        <td>
                          <ActionBtn
                            className="restart"
                            disabled={actionLoading === `${activeTab}-${proc.name}-restart`}
                            onClick={() => handlePm2Action(activeTab, proc.name, 'restart')}
                          >
                            {actionLoading === `${activeTab}-${proc.name}-restart` ? '...' : '🔄'}
                          </ActionBtn>
                          <ActionBtn
                            className="stop"
                            disabled={actionLoading === `${activeTab}-${proc.name}-stop`}
                            onClick={() => handlePm2Action(activeTab, proc.name, 'stop')}
                          >
                            {actionLoading === `${activeTab}-${proc.name}-stop` ? '...' : '⏹'}
                          </ActionBtn>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </StyledTable>
              </TableContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: colors.textDim }}>
                프로세스 정보를 불러오는 중...
              </div>
            )}
          </SectionContainer>

          {/* 프로세스 이벤트 타임라인 */}
          <SectionContainer>
            <SectionTitle>🕒 프로세스 이벤트 (최근 7일)</SectionTitle>
            {procEventStats.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {procEventStats.slice(0, 6).map(s => (
                  <div key={s.process_name} style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    background: 'rgba(7,18,35,0.5)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    fontSize: '0.78rem',
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                  }}>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{s.process_name}</span>
                    {s.restarts_24h > 0 && <span style={{ color: colors.warning }}>🔄 {s.restarts_24h}</span>}
                    {s.crashes_24h > 0 && <span style={{ color: colors.dangerLight }}>💥 {s.crashes_24h}</span>}
                    <span style={{ color: colors.textDim, fontSize: '0.7rem' }}>(24h)</span>
                  </div>
                ))}
              </div>
            )}
            {procEvents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.5rem', color: colors.textDim, fontSize: '0.86rem' }}>
                ✅ 최근 7일간 이벤트 없음
              </div>
            ) : (
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                <StyledTable>
                  <thead>
                    <tr>
                      <th>시각</th>
                      <th>프로세스</th>
                      <th>이벤트</th>
                      <th>상태 변화</th>
                      <th style={{ textAlign: 'right' }}>재시작</th>
                    </tr>
                  </thead>
                  <tbody>
                    {procEvents.slice(0, 50).map(ev => {
                      const t = new Date(ev.ts);
                      const evColor =
                        ev.event_type === 'restart' ? colors.warning :
                        ev.event_type === 'errored' || ev.event_type === 'crashed' ? colors.danger :
                        ev.event_type === 'online' ? colors.successLight :
                        ev.event_type === 'stopped' || ev.event_type === 'removed' ? colors.textDim :
                        '#fff';
                      return (
                        <tr key={ev.id}>
                          <td style={{ color: colors.textDim, fontFamily: 'Fira Code, monospace', fontSize: '0.74rem', whiteSpace: 'nowrap' }}>
                            {t.getMonth() + 1}/{t.getDate()} {String(t.getHours()).padStart(2, '0')}:{String(t.getMinutes()).padStart(2, '0')}:{String(t.getSeconds()).padStart(2, '0')}
                          </td>
                          <td style={{ fontWeight: 600 }}>{ev.process_name}</td>
                          <td>
                            <StatPill style={{ background: `${evColor}1f`, color: evColor, border: `1px solid ${evColor}40` }}>
                              {ev.event_type}
                            </StatPill>
                          </td>
                          <td style={{ color: colors.textMuted, fontSize: '0.78rem' }}>
                            {ev.old_status && <span>{ev.old_status} →</span>} {ev.new_status || '—'}
                          </td>
                          <td style={{ textAlign: 'right', color: colors.textDim }}>{ev.restarts ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </StyledTable>
              </div>
            )}
          </SectionContainer>

          <SectionContainer>
            <SectionTitle>📋 PM2 로그 뷰어</SectionTitle>
            <LogControls>
              <Select value={logProcess} onChange={e => setLogProcess(e.target.value)}>
                <option value="">전체 프로세스</option>
                {(pm2Status[activeTab] || []).map(p => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </Select>
              <LogBtn active={logType === 'error'} className="error" onClick={() => setLogType('error')}>
                ❌ 에러 로그
              </LogBtn>
              <LogBtn active={logType === 'out'} onClick={() => setLogType('out')}>
                📝 표준 출력
              </LogBtn>
              <Select value={logLines} onChange={e => setLogLines(parseInt(e.target.value))}>
                <option value={30}>30줄</option>
                <option value={50}>50줄</option>
                <option value={100}>100줄</option>
                <option value={200}>200줄</option>
              </Select>
              <input
                type="text"
                value={logSearch}
                onChange={e => setLogSearch(e.target.value)}
                placeholder="🔍 키워드 검색"
                style={{
                  padding: '6px 12px',
                  background: 'rgba(7,18,35,0.7)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: '0.78rem',
                  fontFamily: 'inherit',
                  minWidth: 160,
                }}
              />
              <LogBtn onClick={fetchLogs}>🔄 새로고침</LogBtn>
              <LogBtn
                onClick={() => {
                  const params = new URLSearchParams({ type: logType, lines: 1000 });
                  if (logProcess) params.set('process', logProcess);
                  window.open(`/api/admin/server/pm2/${activeTab}/logs/download?${params}`, '_blank');
                }}
              >📥 다운로드</LogBtn>
              {logs && (() => {
                const errorCount = (logs.match(/error|Error|ERROR|FATAL|crash|TypeError|ReferenceError|SyntaxError|ECONNREFUSED/gi) || []).length;
                const warnCount = (logs.match(/warn|Warn|WARN|deprecated|timeout/gi) || []).length;
                return <>
                  {errorCount > 0 && <StatPill style={{ background: 'rgba(248,113,113,0.16)', color: colors.dangerLight, border: '1px solid rgba(248,113,113,0.3)' }}>🚨 에러 {errorCount}건</StatPill>}
                  {warnCount > 0 && <StatPill style={{ background: 'rgba(251,191,36,0.16)', color: colors.warning, border: '1px solid rgba(251,191,36,0.3)' }}>⚠️ 경고 {warnCount}건</StatPill>}
                  {errorCount === 0 && warnCount === 0 && <StatPill style={{ background: 'rgba(34,197,94,0.16)', color: colors.successLight, border: '1px solid rgba(34,197,94,0.3)' }}>✅ 정상</StatPill>}
                </>;
              })()}
            </LogControls>
            <LogContainer>
              {logs && (() => {
                const allLines = logs.split('\n');
                const matched = logSearch
                  ? allLines.filter(line => line.toLowerCase().includes(logSearch.toLowerCase()))
                  : allLines;
                if (logSearch && matched.length === 0) {
                  return <div style={{ padding: 12, color: colors.textDim }}>🔍 '{logSearch}' 검색 결과 없음 ({allLines.length}줄 중)</div>;
                }
                return matched.map((line, i) => {
                  const isError = /error|Error|ERROR|ECONNREFUSED|ENOTFOUND|ETIMEDOUT|FATAL|fatal|crash|Crash|reject|Reject|uncaught|Uncaught|exception|Exception|failed|FAILED|Cannot|cannot|TypeError|ReferenceError|SyntaxError/.test(line);
                  const isWarn = /warn|Warn|WARN|deprecated|Deprecated|timeout|Timeout/.test(line);
                  const baseStyle = isError
                    ? { background: 'rgba(248, 113, 113, 0.12)', color: colors.dangerLight, borderLeft: '3px solid #f87171', paddingLeft: '8px', display: 'block' }
                    : isWarn
                    ? { background: 'rgba(251, 191, 36, 0.1)', color: colors.warning, borderLeft: '3px solid #fbbf24', paddingLeft: '8px', display: 'block' }
                    : { display: 'block' };
                  // 검색어 하이라이트
                  if (logSearch) {
                    const re = new RegExp(`(${logSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                    const parts = line.split(re);
                    return (
                      <span key={i} style={baseStyle}>
                        {parts.map((p, idx) =>
                          re.test(p)
                            ? <mark key={idx} style={{ background: 'rgba(1,188,214,0.3)', color: '#fff', padding: '0 2px', borderRadius: 2 }}>{p}</mark>
                            : <span key={idx}>{p}</span>
                        )}
                      </span>
                    );
                  }
                  return <span key={i} style={baseStyle}>{line}</span>;
                });
              })()}
            </LogContainer>
          </SectionContainer>

          {/* 접속자 통계 차트 — 서비스별 */}
          <SectionContainer>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.05)', flexWrap: 'wrap', gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                👥 서비스별 접속자 통계
              </h2>
              <div style={{ display: 'flex', gap: 4, padding: 3, background: 'rgba(7,18,35,0.5)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10 }}>
                {[['daily', '일별(30일)'], ['weekly', '주별(12주)'], ['monthly', '월별(12개월)']].map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVisitRange(v)}
                    style={{
                      padding: '5px 12px', border: 0, borderRadius: 7,
                      background: visitRange === v ? `${colors.primary}22` : 'transparent',
                      color: visitRange === v ? colors.primaryLight : colors.textMuted,
                      fontSize: '0.78rem',
                      fontWeight: visitRange === v ? 700 : 500,
                      fontFamily: 'inherit', cursor: 'pointer',
                    }}
                  >{label}</button>
                ))}
              </div>
            </div>

            {/* 서비스별 합산 KPI */}
            {visitServices.length > 0 && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                {visitServices.map((s, i) => {
                  const palette = ['#01BCD6', '#34d399', '#a78bfa', '#fbbf24', '#f472b6', '#60a5fa', '#22c55e'];
                  const color = palette[i % palette.length];
                  return (
                    <div key={s.system_id} style={{
                      flex: '1 1 140px', minWidth: 140,
                      padding: '10px 12px',
                      background: 'rgba(7,18,35,0.5)',
                      border: `1px solid ${color}33`,
                      borderLeft: `3px solid ${color}`,
                      borderRadius: 10,
                    }}>
                      <div style={{ fontSize: '0.7rem', color: colors.textDim, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>{s.system_id}</div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 800, color, fontFeatureSettings: '"tnum"' }}>
                        {Number(s.total).toLocaleString()}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {visitData.length === 0 || visitServices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: colors.textDim, fontSize: '0.86rem' }}>
                <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>📊</div>
                선택 기간에 수집된 방문 데이터가 없습니다.
              </div>
            ) : (
              <div style={{ height: 320 }}>
                <Line
                  data={(() => {
                    const palette = ['#01BCD6', '#34d399', '#a78bfa', '#fbbf24', '#f472b6', '#60a5fa', '#22c55e'];
                    const fmtBucket = (iso) => {
                      const d = new Date(iso);
                      if (visitRange === 'monthly') return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}`;
                      return `${d.getMonth() + 1}/${d.getDate()}`;
                    };
                    return {
                      labels: visitData.map(p => fmtBucket(p.bucket)),
                      datasets: visitServices.map((s, i) => {
                        const color = palette[i % palette.length];
                        return {
                          label: s.system_id,
                          data: visitData.map(p => {
                            const found = (p.services || []).find(x => x.system_id === s.system_id);
                            return found ? found.visits : 0;
                          }),
                          borderColor: color,
                          backgroundColor: color + '22',
                          fill: false,
                          tension: 0.3,
                          pointRadius: 3,
                          pointHoverRadius: 5,
                          borderWidth: 2,
                        };
                      }),
                    };
                  })()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                      legend: { position: 'bottom', labels: { color: 'rgba(255,255,255,0.6)', font: { size: 11 }, boxWidth: 12, usePointStyle: true } },
                      tooltip: {
                        backgroundColor: 'rgba(7,18,35,0.95)',
                        borderColor: 'rgba(139,210,244,0.2)',
                        borderWidth: 1,
                      },
                    },
                    scales: {
                      x: { grid: { display: false }, ticks: { color: 'rgba(255,255,255,0.4)', font: { size: 9 } } },
                      y: { beginAtZero: true, grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: 'rgba(255,255,255,0.4)', font: { size: 9 } } },
                    },
                  }}
                />
              </div>
            )}
          </SectionContainer>

          {/* 알람 이력 섹션 */}
          <SectionContainer id="alerts-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.05)', flexWrap: 'wrap', gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                🚨 알람 이력
              </h2>
              <div style={{ display: 'flex', gap: 4, padding: 3, background: 'rgba(7,18,35,0.5)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10 }}>
                {[[false, `활성 ${alertCounts.active || 0}`], [true, `전체`]].map(([v, label]) => (
                  <button
                    key={String(v)}
                    type="button"
                    onClick={() => setShowAllAlerts(v)}
                    style={{
                      padding: '5px 14px',
                      border: 0,
                      borderRadius: 7,
                      background: showAllAlerts === v ? `${colors.primary}22` : 'transparent',
                      color: showAllAlerts === v ? colors.primaryLight : colors.textMuted,
                      fontSize: '0.78rem',
                      fontWeight: showAllAlerts === v ? 700 : 500,
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                    }}
                  >{label}</button>
                ))}
              </div>
            </div>
            {alerts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.5rem', color: colors.textDim, fontSize: '0.86rem' }}>
                ✅ {showAllAlerts ? '알람 이력이 없습니다.' : '활성 알람이 없습니다.'}
              </div>
            ) : (
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                <StyledTable>
                  <thead>
                    <tr>
                      <th>심각도</th>
                      <th>발생 시각</th>
                      <th>서버</th>
                      <th>룰</th>
                      <th>측정값</th>
                      <th>지속</th>
                      <th>상태</th>
                      <th>액션</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.map(a => {
                      const sev = a.severity === 'critical' ? colors.danger : a.severity === 'warning' ? colors.warning : colors.primaryLight;
                      const stColor =
                        a.status === 'active' ? colors.danger :
                        a.status === 'acknowledged' ? colors.warning :
                        colors.successLight;
                      const t = new Date(a.fired_at);
                      const dur = a.duration_sec || 0;
                      return (
                        <tr key={a.id}>
                          <td><StatPill style={{ background: `${sev}1f`, color: sev, border: `1px solid ${sev}40` }}>{a.severity.toUpperCase()}</StatPill></td>
                          <td style={{ color: colors.textMuted, fontFamily: 'Fira Code, monospace', fontSize: '0.74rem', whiteSpace: 'nowrap' }}>
                            {t.getMonth() + 1}/{t.getDate()} {String(t.getHours()).padStart(2, '0')}:{String(t.getMinutes()).padStart(2, '0')}
                          </td>
                          <td style={{ fontWeight: 600 }}>{a.server_id}</td>
                          <td style={{ fontSize: '0.82rem' }}>{a.rule_name}</td>
                          <td style={{ color: sev, fontWeight: 700, fontFeatureSettings: '"tnum"' }}>
                            {a.value != null ? `${parseFloat(a.value).toFixed(1)}` : '—'}
                            {a.threshold != null && <span style={{ color: colors.textDim, fontSize: '0.74rem', fontWeight: 500 }}> / {a.threshold}</span>}
                          </td>
                          <td style={{ color: colors.textMuted, fontSize: '0.78rem' }}>
                            {dur >= 3600 ? `${Math.floor(dur/3600)}h ${Math.floor((dur%3600)/60)}m` : dur >= 60 ? `${Math.floor(dur/60)}m ${dur%60}s` : `${dur}s`}
                          </td>
                          <td><StatPill style={{ background: `${stColor}1f`, color: stColor, border: `1px solid ${stColor}40` }}>{a.status}</StatPill></td>
                          <td>
                            {a.status === 'active' && (
                              <ActionBtn className="restart" onClick={() => acknowledgeAlert(a.id)} title="확인">👁️</ActionBtn>
                            )}
                            {(a.status === 'active' || a.status === 'acknowledged') && (
                              <ActionBtn className="stop" onClick={() => resolveAlert(a.id)} title="해결">✓</ActionBtn>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </StyledTable>
              </div>
            )}
          </SectionContainer>
          </>
          )}
        </>
      ) : (
        <SectionContainer>
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: colors.textDim }}>
            {currentMetrics?.error ? (
              <>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
                <div style={{ color: colors.dangerLight }}>서버 연결 실패: {currentMetrics.message || currentMetrics.error}</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                <div>서버 메트릭을 불러오는 중...</div>
              </>
            )}
          </div>
        </SectionContainer>
      )}

        </>
      )}

      {/* 운영 설정 모달 (룰 + 채널) */}
      {showSettings && (
        <ModalBackdrop onClick={() => { setShowSettings(false); setEditingRule(null); setEditingChannel(null); }}>
          <ModalCard onClick={e => e.stopPropagation()} style={{ width: 'min(820px, 100%)' }}>
            <ModalHeader>
              <ModalTitle>🔔 알람·알림 채널 설정</ModalTitle>
              <ModalClose onClick={() => { setShowSettings(false); setEditingRule(null); setEditingChannel(null); }}>×</ModalClose>
            </ModalHeader>
            <ModalBody>
              <SubTabBar style={{ marginTop: 0 }}>
                <SubTab type="button" active={settingsTab === 'rules'} onClick={() => { setSettingsTab('rules'); setEditingRule(null); }}>
                  📜 알람 룰 <span className="badge">{alertRules.length}</span>
                </SubTab>
                <SubTab type="button" active={settingsTab === 'channels'} onClick={() => { setSettingsTab('channels'); setEditingChannel(null); }}>
                  📡 알림 채널 <span className="badge">{channels.length}</span>
                </SubTab>
              </SubTabBar>

              {settingsTab === 'rules' && (
                <>
                  {editingRule ? (
                    <RuleForm
                      rule={editingRule}
                      servers={servers}
                      onCancel={() => setEditingRule(null)}
                      onSave={saveRule}
                    />
                  ) : (
                    <>
                      <div style={{ marginBottom: 12, textAlign: 'right' }}>
                        <PrimaryBtn type="button" onClick={() => setEditingRule({
                          name: '', server_id: null, metric: 'cpu', comparator: '>',
                          threshold: 80, duration_sec: 60, severity: 'warning', enabled: true,
                        })}>+ 새 룰</PrimaryBtn>
                      </div>
                      <TableContainer>
                        <StyledTable>
                          <thead><tr>
                            <th>이름</th><th>대상</th><th>조건</th><th>지속</th><th>심각도</th><th>활성</th><th>액션</th>
                          </tr></thead>
                          <tbody>
                            {alertRules.map(r => (
                              <tr key={r.id}>
                                <td style={{ fontWeight: 600 }}>{r.name}</td>
                                <td style={{ color: colors.textDim, fontSize: '0.78rem' }}>{r.server_id || '전체'}</td>
                                <td style={{ fontFamily: 'Fira Code, monospace', fontSize: '0.78rem' }}>{r.metric} {r.comparator} {r.threshold}</td>
                                <td>{r.duration_sec}s</td>
                                <td><StatPill style={{
                                  background: r.severity === 'critical' ? `${colors.danger}1f` : r.severity === 'warning' ? `${colors.warning}1f` : `${colors.primary}1f`,
                                  color: r.severity === 'critical' ? colors.danger : r.severity === 'warning' ? colors.warning : colors.primaryLight,
                                  border: `1px solid ${r.severity === 'critical' ? colors.danger : r.severity === 'warning' ? colors.warning : colors.primary}40`,
                                }}>{r.severity}</StatPill></td>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={r.enabled}
                                    onChange={e => saveRule({ id: r.id, enabled: e.target.checked })}
                                    style={{ cursor: 'pointer' }}
                                  />
                                </td>
                                <td>
                                  <IconBtn onClick={() => setEditingRule(r)} title="편집">✏️</IconBtn>
                                  <IconBtn className="danger" onClick={() => deleteRule(r.id)} title="삭제">🗑️</IconBtn>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </StyledTable>
                      </TableContainer>
                    </>
                  )}
                </>
              )}

              {settingsTab === 'channels' && (
                <>
                  {editingChannel ? (
                    <ChannelForm
                      channel={editingChannel}
                      onCancel={() => setEditingChannel(null)}
                      onSave={saveChannel}
                    />
                  ) : (
                    <>
                      <div style={{ marginBottom: 12, textAlign: 'right' }}>
                        <PrimaryBtn type="button" onClick={() => setEditingChannel({
                          name: '', type: 'webhook', url: '', min_severity: 'warning', enabled: true,
                        })}>+ 새 채널</PrimaryBtn>
                      </div>
                      {channels.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: colors.textDim, fontSize: '0.86rem' }}>
                          아직 등록된 알림 채널이 없습니다.<br />
                          <span style={{ fontSize: '0.74rem' }}>Slack/Discord webhook URL을 추가하면 알람 발생 시 자동 송출됩니다.</span>
                        </div>
                      ) : (
                        <TableContainer>
                          <StyledTable>
                            <thead><tr>
                              <th>이름</th><th>타입</th><th>URL</th><th>최소 심각도</th><th>활성</th><th>액션</th>
                            </tr></thead>
                            <tbody>
                              {channels.map(c => (
                                <tr key={c.id}>
                                  <td style={{ fontWeight: 600 }}>{c.name}</td>
                                  <td style={{ fontFamily: 'Fira Code, monospace', fontSize: '0.78rem', color: colors.primaryLight }}>{c.type}</td>
                                  <td style={{ fontFamily: 'Fira Code, monospace', fontSize: '0.74rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: colors.textMuted }} title={c.url}>{c.url}</td>
                                  <td>{c.min_severity}</td>
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={c.enabled}
                                      onChange={e => saveChannel({ id: c.id, enabled: e.target.checked })}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </td>
                                  <td>
                                    <IconBtn onClick={() => testChannel(c.id)} title="테스트 발송">🧪</IconBtn>
                                    <IconBtn onClick={() => setEditingChannel(c)} title="편집">✏️</IconBtn>
                                    <IconBtn className="danger" onClick={() => deleteChannel(c.id)} title="삭제">🗑️</IconBtn>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </StyledTable>
                        </TableContainer>
                      )}
                    </>
                  )}
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <GhostBtn onClick={() => { setShowSettings(false); setEditingRule(null); setEditingChannel(null); }}>닫기</GhostBtn>
            </ModalFooter>
          </ModalCard>
        </ModalBackdrop>
      )}

      {showRegistry && (
        <ModalBackdrop onClick={() => { setShowRegistry(false); setEditingId(null); }}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingId === null ? '⚙️ 서버 등록 관리' : (editingId === '' ? '➕ 새 서버 추가' : `✏️ 서버 편집 — ${editingId}`)}
              </ModalTitle>
              <ModalClose onClick={() => { setShowRegistry(false); setEditingId(null); }}>×</ModalClose>
            </ModalHeader>

            <ModalBody>
              {editingId === null ? (
                <>
                  {servers.length > 0 && (
                    <>
                      <SearchBar>
                        <span className="search-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="7" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                          </svg>
                        </span>
                        <input
                          type="text"
                          value={registryQuery}
                          onChange={e => setRegistryQuery(e.target.value)}
                          placeholder="이름 / ID / IP / Host 로 검색…"
                          autoFocus
                        />
                        {registryQuery && (
                          <button
                            type="button"
                            className="search-clear"
                            onClick={() => setRegistryQuery('')}
                            aria-label="검색어 지우기"
                          >×</button>
                        )}
                      </SearchBar>
                      <SearchMeta>
                        <span>
                          전체 <strong>{servers.length}</strong>개
                          {registryQuery && <> · 검색 결과 <strong>{filteredServers.length}</strong>개</>}
                        </span>
                      </SearchMeta>
                    </>
                  )}
                  <RegistryList>
                    {servers.length === 0 ? (
                      <div style={{ padding: '32px', textAlign: 'center', color: colors.textDim }}>
                        등록된 서버가 없습니다.
                      </div>
                    ) : filteredServers.length === 0 ? (
                      <div style={{ padding: '32px', textAlign: 'center', color: colors.textDim, fontSize: '0.88rem' }}>
                        🔍 '{registryQuery}'와(과) 일치하는 서버가 없습니다.
                      </div>
                    ) : filteredServers.map((s, idx) => {
                      const sc = getServerColor(s, idx);
                      return (
                        <RegistryRow key={s.server_id} color={sc}>
                          <span style={{ fontSize: '1.4rem' }}>{s.emoji || '🖥️'}</span>
                          <RowMeta>
                            <span className="name">{s.name}</span>
                            <span className="sub">{s.server_id} · {s.ip}:{s.ssh_port}</span>
                          </RowMeta>
                          <RowBadge local={s.auth_type === 'local'}>
                            {s.auth_type === 'local' ? 'LOCAL' : s.auth_type === 'key' ? 'SSH KEY' : 'SSH'}
                          </RowBadge>
                          <RowActions>
                            <IconBtn onClick={() => openEditServer(s)} title="편집">✏️</IconBtn>
                            <IconBtn className="danger" onClick={() => deleteServer(s.server_id)} title="삭제">🗑️</IconBtn>
                          </RowActions>
                        </RegistryRow>
                      );
                    })}
                  </RegistryList>
                  <div style={{ marginTop: '16px', textAlign: 'right' }}>
                    <PrimaryBtn type="button" onClick={openNewServer}>+ 새 서버 추가</PrimaryBtn>
                  </div>
                </>
              ) : (
                <FormGrid>
                  <FormField>
                    <span>서버 ID *</span>
                    <FormInput
                      value={form.server_id}
                      onChange={e => setForm({ ...form, server_id: e.target.value })}
                      placeholder="예: master, dev01"
                      disabled={editingId !== ''}
                      maxLength={50}
                    />
                  </FormField>
                  <FormField>
                    <span>이모지</span>
                    <FormInput
                      value={form.emoji}
                      onChange={e => setForm({ ...form, emoji: e.target.value })}
                      placeholder="🖥️"
                      maxLength={4}
                    />
                  </FormField>
                  <FormField full>
                    <span>표시 이름 *</span>
                    <FormInput
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="예: master-antsnest-kr"
                    />
                  </FormField>
                  <FormField>
                    <span>IP *</span>
                    <FormInput
                      value={form.ip}
                      onChange={e => setForm({ ...form, ip: e.target.value })}
                      placeholder="115.68.x.x"
                    />
                  </FormField>
                  <FormField>
                    <span>Host *</span>
                    <FormInput
                      value={form.host}
                      onChange={e => setForm({ ...form, host: e.target.value })}
                      placeholder="대개 IP와 동일"
                    />
                  </FormField>
                  <FormField>
                    <span>인증 방식</span>
                    <FormSelect
                      value={form.auth_type}
                      onChange={e => setForm({ ...form, auth_type: e.target.value })}
                    >
                      <option value="password">SSH 비밀번호</option>
                      <option value="key">SSH 개인키</option>
                      <option value="local">로컬 (이 서버 자체)</option>
                    </FormSelect>
                  </FormField>
                  <FormField>
                    <span>SSH 포트</span>
                    <FormInput
                      type="number"
                      value={form.ssh_port}
                      onChange={e => setForm({ ...form, ssh_port: parseInt(e.target.value) || 22 })}
                      disabled={form.auth_type === 'local'}
                    />
                  </FormField>
                  <FormField>
                    <span>SSH 사용자</span>
                    <FormInput
                      value={form.ssh_username}
                      onChange={e => setForm({ ...form, ssh_username: e.target.value })}
                      disabled={form.auth_type === 'local'}
                    />
                  </FormField>
                  {form.auth_type === 'password' && (
                    <FormField full>
                      <span>SSH 비밀번호 {editingId !== '' && '(수정 시에만 입력)'}</span>
                      <FormInput
                        type="password"
                        value={form.ssh_password}
                        onChange={e => setForm({ ...form, ssh_password: e.target.value })}
                        placeholder={editingId !== '' ? '비워두면 기존 비밀번호 유지' : '••••••••'}
                        autoComplete="new-password"
                      />
                    </FormField>
                  )}
                  {form.auth_type === 'key' && (
                    <>
                      <FormField full>
                        <span>SSH 개인키 {editingId !== '' && '(수정 시에만 입력)'}</span>
                        <FormTextarea
                          value={form.ssh_private_key}
                          onChange={e => setForm({ ...form, ssh_private_key: e.target.value })}
                          placeholder={editingId !== ''
                            ? '비워두면 기존 개인키 유지'
                            : '-----BEGIN OPENSSH PRIVATE KEY-----\n...\n-----END OPENSSH PRIVATE KEY-----'}
                          spellCheck={false}
                          autoComplete="off"
                        />
                        <FormHint>
                          PEM 전문을 줄바꿈 그대로 붙여넣으세요. 저장 시 암호화되며 목록·조회 API로는 다시 내려가지 않습니다.
                        </FormHint>
                      </FormField>
                      <FormField full>
                        <span>개인키 암호 (passphrase) — 없으면 비워두세요</span>
                        <FormInput
                          type="password"
                          value={form.ssh_key_passphrase}
                          onChange={e => setForm({ ...form, ssh_key_passphrase: e.target.value })}
                          placeholder="키에 암호가 걸려 있을 때만"
                          autoComplete="new-password"
                        />
                      </FormField>
                    </>
                  )}
                  <FormField>
                    <span>표시 색상</span>
                    <FormInput
                      type="color"
                      value={form.color}
                      onChange={e => setForm({ ...form, color: e.target.value })}
                      style={{ height: '42px', padding: '4px' }}
                    />
                  </FormField>
                  <FormField>
                    <span>표시 순서</span>
                    <FormInput
                      type="number"
                      value={form.display_order}
                      onChange={e => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })}
                    />
                  </FormField>
                </FormGrid>
              )}
            </ModalBody>

            <ModalFooter>
              {editingId === null ? (
                <GhostBtn onClick={() => setShowRegistry(false)}>닫기</GhostBtn>
              ) : (
                <>
                  <GhostBtn onClick={() => setEditingId(null)}>← 목록</GhostBtn>
                  {form.auth_type !== 'local' && (
                    <GhostBtn onClick={testSshConnection} disabled={testingSsh}>
                      {testingSsh ? '테스트 중...' : '🔌 연결 테스트'}
                    </GhostBtn>
                  )}
                  <PrimaryBtn onClick={saveServer} disabled={savingServer}>
                    {savingServer ? '저장 중...' : (editingId === '' ? '추가' : '저장')}
                  </PrimaryBtn>
                </>
              )}
            </ModalFooter>
          </ModalCard>
        </ModalBackdrop>
      )}
    </DashboardContainer>
  );
}
